import React, { useEffect, useRef, useState, useTransition } from "react";
import { readHistoryEntryService } from "../../services/readHistoryEntryService";
import { useAuth } from "../../context/AuthContext";
import { BlobLoader } from "../../components/loader";

// ============================================================================
// Logging helpers
// ============================================================================
const DEV = (() => {
  try {
    return process.env.NODE_ENV !== "production";
  } catch {
    return false;
  }
})();

const LINE = "════════════════════════════════════";

function logBlock(title, lines = [], level = "log") {
  console[level]([LINE, title, LINE, ...lines, LINE].join("\n"));
}

function audioErrorCause(err, mediaError) {
  if (err?.name === "NotAllowedError") return "browser autoplay policy (needs a user click first)";
  if (err?.name === "NotSupportedError" || mediaError?.code === 4) return "audio file missing or unsupported format";
  if (mediaError?.code === 2) return "network error while downloading audio";
  if (mediaError?.code === 3) return "audio file corrupt or undecodable";
  if (err?.name === "AbortError") return "playback interrupted by another audio";
  return "unclassified";
}

function logAudioError(context, url, err, mediaError) {
  if (err?.name === "AbortError") return;
  console.error(`❌ AUDIO ERROR (${context}): ${url} — ${audioErrorCause(err, mediaError)}`, err);
  fetch(url, { method: "HEAD" })
    .then((r) => console.warn(`   ↳ HTTP ${r.status} | Content-Type: ${r.headers.get("content-type")} | Length: ${r.headers.get("content-length")}`))
    .catch(() => console.warn("   ↳ status check blocked (CORS / network)"));
}
// ============================================================================
// SVG summary (reading layers, hand markers, content problems)
// ============================================================================
function logSvgSummary(order, handMarkers, nestedExcluded) {
  const issues = [];
  if (!order.length) issues.push("no reading groups found");
  if (order.length !== handMarkers.length) {
    issues.push(`${order.length} reading layers vs ${handMarkers.length} hand markers`);
  }
  order.forEach((item) => {
    if (!item.markerEl) issues.push(`Layer ${item.index}: no hand marker`);
    if (!item.audioPath) issues.push(`Layer ${item.index}: no audio path`);
  });
  const dupAudio = {};
  order.forEach((item) => {
    if (item.audioPath) (dupAudio[item.audioPath] = dupAudio[item.audioPath] || []).push(item.index);
  });
  Object.entries(dupAudio).forEach(([p, layers]) => {
    if (layers.length > 1) issues.push(`Layers ${layers.join(", ")} share audio: ${p}`);
  });

  logBlock(
    "🔍 SVG SUMMARY",
    [
      `Reading layers: ${order.length}`,
      `Hand markers: ${handMarkers.length}`,
      `Nested groups excluded: ${nestedExcluded}`,
      issues.length ? "Issues:" : "✅ No issues",
      ...issues.map((i) => `  • ${i}`),
    ],
    issues.length ? "warn" : "log"
  );
}

// ============================================================================
// OCR + hand-marker text test (word-retrieval stage: no translation, no overlay)
// ============================================================================
const SVG_NS = "http://www.w3.org/2000/svg";
const XLINK_NS = "http://www.w3.org/1999/xlink";

const OCR_MIN_CONFIDENCE = 50;
const OCR_SCALE = 3;
const QUALITY_MIN = 0.6;

const OCR_CONFIG = {
  enabled: true,
  handTextOnly: true,
  lang: "eng",
  retrievalOnly: false,
  debugRunAllPasses: true,

  ocrScale: OCR_SCALE,
  minLayerSide: 500,
  maxLayerSide: 2400,
  canvasPad: 24,
  layerExpandRatio: 0.03,
  lightBg: "#ffffff",
  darkBg: "#262626",
  retryOnDark: true,

  useSvgText: true,
  psms: [11, 6],
  earlyExitConfidence: 90,
  refineEnabled: true,
  refinePsm: 8,
  refineTargetTextPx: 48,
  refineMargin: 16,
  psmMaxFailures: 3,

  minTextHeightPx: 12,

  wordGapRatio: 1.1,
  noSpaceGapRatio: 0.15,

  refineExtents: true,
  refineMaxXRatio: 0.6,
  refineMaxYRatio: 0.3,
  refineInkFraction: 0.05,
  uniformStdMax: 28,
  coverPadXRatio: 0.4,
  coverPadYRatio: 0.3,
  coverPadXRatioArt: 0.15,
  coverPadYRatioArt: 0.08,
  maxGrowRatio: 1.45,

  bubbleMode: true,
  bubbleShape: "ellipse",              // "ellipse" or "rect" (use rect for square boxes)
  bubbleCoverScale: 1.0,               // cover size vs detected interior (1.0 = exactly the interior)
  bubbleTextScale: { w: 0.66, h: 0.58 }, // Tamil area as a fraction of the interior
  bubbleSearchPx: 700,                 // working resolution for the detection
  bubbleFillTolerance: 45,             // how close to the bubble colour counts as "inside"


  // translation UI (compact bubble above the English text)
  transWidthRatio: 1.25,     // available width = English text width × this
  transMinWidthRatio: 0.2,   // ...but never narrower than this share of the SVG width
  transEdgeMarginRatio: 0.01,    // try fewer lines down to this share of the start size before wrapping more
  transMaxLines: 3,
  transLineHeight: 1.25,
  transPadXRatio: 0.7,       // padding as a multiple of the final font size
  transPadYRatio: 0.4,
  transTail: true,           // small pointer from the bubble to the English text
  transBg: "#ffffff",
  transFg: "#222222",
  transDebug: false,          // dashed red outline around the detected English text. Set false when done
  fontSizePx: 11,
  imageLoadTimeoutMs: 8000,
  translateEndpoint: "https://translate.googleapis.com/translate_a/single",
  translateTimeoutMs: 8000,
  prefetchInEnglish: true,
  fontFamily:
    "'Noto Sans','Noto Sans Tamil','Noto Sans Telugu','Noto Sans Malayalam','Noto Sans Devanagari','Nirmala UI',Latha,Gautami,Kartika,Mangal,system-ui,sans-serif",
};

const ocrCache = new Map();
const ocrLayerStore = {};
const translationCache = new Map();
let ocrWorkerPromise = null;
const ocrPsmFailures = new Map();

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getImageHref = (img) =>
  img.getAttribute("href") || img.getAttributeNS(XLINK_NS, "href") || img.getAttribute("xlink:href");

function getOcrWorker() {
  if (!ocrWorkerPromise) {
    ocrWorkerPromise = (async () => {
      const mod = await import("tesseract.js");
      const createWorker = mod.createWorker || mod.default?.createWorker;
      const worker = await createWorker(OCR_CONFIG.lang);
      await worker.setParameters({
        tessedit_pageseg_mode: "8",
        preserve_interword_spaces: "1",
        user_defined_dpi: "300",
      });
      return worker;
    })().catch((err) => {
      ocrWorkerPromise = null;
      throw err;
    });
  }
  return ocrWorkerPromise;
}

export async function terminateLayeredSVGOcrWorker() {
  if (!ocrWorkerPromise) return;
  try {
    const worker = await ocrWorkerPromise;
    await worker.terminate();
  } catch {
    /* ignore */
  }
  ocrWorkerPromise = null;
}

function getSvgViewBox(svgEl) {
  const vb = svgEl.viewBox && svgEl.viewBox.baseVal;
  if (vb && vb.width > 0 && vb.height > 0) return { x: vb.x, y: vb.y, width: vb.width, height: vb.height };
  const num = (v) => (v && !String(v).trim().endsWith("%") ? parseFloat(v) : NaN);
  const w = num(svgEl.getAttribute("width"));
  const h = num(svgEl.getAttribute("height"));
  if (w > 0 && h > 0) return { x: 0, y: 0, width: w, height: h };
  try {
    const bb = svgEl.getBBox();
    if (bb.width > 0 && bb.height > 0) return { x: bb.x, y: bb.y, width: bb.width, height: bb.height };
  } catch {
    /* not rendered */
  }
  return null;
}

async function waitForSvgRendered(svgEl) {
  const probes = Array.from(svgEl.querySelectorAll("image")).map((img) => {
    const href = getImageHref(img);
    if (!href) return Promise.resolve();
    return new Promise((resolve) => {
      const probe = new Image();
      probe.onload = () => resolve();
      probe.onerror = () => resolve();
      probe.src = href;
    });
  });
  await Promise.race([Promise.all(probes), sleep(OCR_CONFIG.imageLoadTimeoutMs)]);
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

// ---- self-contained clone helpers -------------------------------------------
const MIME_BY_EXT = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  bmp: "image/bmp",
};

function guessMime(url, blobType) {
  if (blobType && blobType.startsWith("image/")) return blobType;
  const m = String(url).split(/[?#]/)[0].match(/\.([a-z0-9]+)$/i);
  return (m && MIME_BY_EXT[m[1].toLowerCase()]) || "image/png";
}

function blobToDataUrl(blob, mime) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob.type === mime ? blob : new Blob([blob], { type: mime }));
  });
}

function loadImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error("The SVG could not be rendered to an image for OCR"));
    im.src = url;
  });
}

async function fetchAsDataUrl(url) {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return await blobToDataUrl(blob, guessMime(url, blob.type));
  } catch (fetchErr) {
    try {
      const im = await loadImageFromUrl(url);
      const c = document.createElement("canvas");
      c.width = im.naturalWidth || 1;
      c.height = im.naturalHeight || 1;
      c.getContext("2d").drawImage(im, 0, 0);
      return c.toDataURL("image/png");
    } catch {
      throw fetchErr;
    }
  }
}

async function inlineExternalImages(clone, src) {
  const base = new URL(src, window.location.href).href;
  const stats = { inlined: 0, failed: 0 };
  await Promise.all(
    Array.from(clone.querySelectorAll("image")).map(async (img) => {
      const href = getImageHref(img);
      if (!href || href.startsWith("data:")) return;
      try {
        const dataUrl = await fetchAsDataUrl(new URL(href, base).href);
        img.setAttribute("href", dataUrl);
        img.removeAttributeNS(XLINK_NS, "href");
        img.removeAttribute("xlink:href");
        stats.inlined += 1;
      } catch {
        stats.failed += 1;
      }
    })
  );
  return stats;
}

function sanitizeCloneForOcr(clone) {
  const removed = [];
  clone.querySelectorAll("foreignObject, script").forEach((n) => {
    removed.push(n.tagName);
    n.remove();
  });
  clone.querySelectorAll("image, feImage").forEach((n) => {
    const h = getImageHref(n);
    if (h && !h.startsWith("data:")) {
      removed.push(n.tagName);
      n.remove();
    }
  });
  clone.querySelectorAll("use").forEach((n) => {
    const h = n.getAttribute("href") || n.getAttribute("xlink:href");
    if (h && !h.startsWith("#")) {
      removed.push("use");
      n.remove();
    }
  });
  const extUrl = () => /url\(\s*['"]?(?!data:|#)[^)]+\)/gi;
  clone.querySelectorAll("style").forEach((s) => {
    const after = s.textContent.replace(/@import[^;]+;/gi, "").replace(extUrl(), "none");
    if (after !== s.textContent) {
      removed.push("style");
      s.textContent = after;
    }
  });
  clone.querySelectorAll("[style]").forEach((n) => {
    const st = n.getAttribute("style");
    if (st && extUrl().test(st)) {
      removed.push("style attr");
      n.setAttribute("style", st.replace(extUrl(), "none"));
    }
  });
  return removed;
}

function canReadCanvasPixels(canvas) {
  try {
    canvas.getContext("2d", { willReadFrequently: true }).getImageData(0, 0, 1, 1);
    return true;
  } catch {
    return false;
  }
}

async function drawCloneToCanvas(clone, w, h, pad, bg) {
  const xml = new XMLSerializer().serializeToString(clone);
  const blobUrl = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml;charset=utf-8" }));
  try {
    const im = await loadImageFromUrl(blobUrl);
    const canvas = document.createElement("canvas");
    canvas.width = w + 2 * pad;
    canvas.height = h + 2 * pad;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.imageSmoothingQuality = "high";
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(im, pad, pad, w, h);
    return canvas;
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

// ---- geometry ---------------------------------------------------------------
function median(arr) {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

function clientRectToViewBox(svgEl, vb, r) {
  try {
    const m = svgEl.getScreenCTM();
    if (m) {
      const inv = m.inverse();
      const p1 = svgEl.createSVGPoint();
      p1.x = r.left;
      p1.y = r.top;
      const a = p1.matrixTransform(inv);
      const p2 = svgEl.createSVGPoint();
      p2.x = r.right;
      p2.y = r.bottom;
      const b = p2.matrixTransform(inv);
      return {
        x: Math.min(a.x, b.x),
        y: Math.min(a.y, b.y),
        width: Math.abs(b.x - a.x),
        height: Math.abs(b.y - a.y),
      };
    }
  } catch {
    /* fall through */
  }
  const s = svgEl.getBoundingClientRect();
  if (!s.width || !s.height) return null;
  const sx = vb.width / s.width;
  const sy = vb.height / s.height;
  return {
    x: vb.x + (r.left - s.left) * sx,
    y: vb.y + (r.top - s.top) * sy,
    width: r.width * sx,
    height: r.height * sy,
  };
}

function findLayerElement(svgEl, groupId) {
  return Array.from(svgEl.querySelectorAll("g[id]")).find((g) => g.id === groupId) || null;
}

function getLayerRegion(svgEl, vb, el) {
  const r = clientRectToViewBox(svgEl, vb, el.getBoundingClientRect());
  if (!r || !(r.width > 0) || !(r.height > 0)) return null;
  const ex = Math.max(r.width, r.height) * OCR_CONFIG.layerExpandRatio;
  const x0 = Math.max(vb.x, r.x - ex);
  const y0 = Math.max(vb.y, r.y - ex);
  const x1 = Math.min(vb.x + vb.width, r.x + r.width + ex);
  const y1 = Math.min(vb.y + vb.height, r.y + r.height + ex);
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

// ---- rendering one layer to a canvas ----------------------------------------
async function prepareOcrBase(svgEl, vb, src) {
  const base = svgEl.cloneNode(true);
  base.querySelectorAll(".lsvg-ocr-overlay").forEach((n) => n.remove());
  base.querySelectorAll('[id^="hand_x5F_"]').forEach((n) => n.remove());
  base.querySelectorAll(".highlight, .boom-pulse").forEach((el) => {
    el.classList.remove("highlight", "boom-pulse");
    el.style.filter = "";
  });
  base.setAttribute("viewBox", `${vb.x} ${vb.y} ${vb.width} ${vb.height}`);
  const stats = await inlineExternalImages(base, src);
  return { base, sanitized: false, imageStats: stats };
}

const ISOLATE_KEEP_TAGS = new Set([
  "defs", "style", "title", "desc", "metadata", "clippath", "mask", "symbol",
  "pattern", "lineargradient", "radialgradient", "filter", "marker",
]);

function isolateLayerInClone(clone, groupId) {
  const target = Array.from(clone.querySelectorAll("g[id]")).find((g) => g.id === groupId);
  if (!target) return false;
  let node = target;
  while (node && node !== clone) {
    const parent = node.parentNode;
    if (!parent || !parent.children) break;
    Array.from(parent.children).forEach((sib) => {
      if (sib === node) return;
      if (ISOLATE_KEEP_TAGS.has(String(sib.tagName).toLowerCase())) return;
      sib.parentNode.removeChild(sib);
    });
    node = parent;
  }
  return true;
}

async function renderRegionCanvas(state, region, ppu, pad, opts) {
  const { groupId = null, bg = OCR_CONFIG.lightBg } = opts || {};
  const iw = Math.max(1, Math.round(region.width * ppu));
  const ih = Math.max(1, Math.round(region.height * ppu));

  const build = () => {
    const clone = state.base.cloneNode(true);
    if (groupId && !isolateLayerInClone(clone, groupId)) return null;
    clone.setAttribute("viewBox", `${region.x} ${region.y} ${region.width} ${region.height}`);
    clone.setAttribute("width", String(iw));
    clone.setAttribute("height", String(ih));
    clone.setAttribute("preserveAspectRatio", "none");
    clone.removeAttribute("x");
    clone.removeAttribute("y");
    clone.removeAttribute("style");
    return clone;
  };

  let clone = build();
  if (!clone) return null;
  let canvas = await drawCloneToCanvas(clone, iw, ih, pad, bg);
  let readable = canReadCanvasPixels(canvas);

  if (!readable && !state.sanitized) {
    sanitizeCloneForOcr(state.base);
    state.sanitized = true;
    clone = build();
    if (!clone) return null;
    canvas = await drawCloneToCanvas(clone, iw, ih, pad, bg);
    readable = canReadCanvasPixels(canvas);
  }
  return { canvas, readable };
}

// ---- OCR words → text regions -----------------------------------------------
const PUNCT_ONLY_RE = /^[-–—−.,:;·•]+$/;
const LEAD_JUNK_RE = /^[^A-Za-z0-9\u00C0-\u024F\u2013\u2014\u2212.\-]+/;
const TRAIL_JUNK_RE = /[^A-Za-z0-9\u00C0-\u024F\u2013\u2014\u2212.!?\-]+$/;
const COMMON_SHORT_WORDS = new Set(
  "a an as at be by do go he if in is it me my no of on or so to up us we and are but can for her him his not one our out the you yes".split(" ")
);

function collectWords(data) {
  let raw = [];
  if (Array.isArray(data.words) && data.words.length) {
    raw = data.words;
  } else {
    const lines =
      data.lines && data.lines.length
        ? data.lines
        : (data.blocks || []).flatMap((b) => (b.paragraphs || []).flatMap((p) => p.lines || []));
    raw = lines.flatMap((l) => (l.words && l.words.length ? l.words : [l]));
  }
  return raw
    .filter((w) => w && w.bbox && String(w.text || "").trim())
    .map((w) => ({
      text: String(w.text).trim(),
      confidence: w.confidence || 0,
      px: { x0: w.bbox.x0, y0: w.bbox.y0, x1: w.bbox.x1, y1: w.bbox.y1 },
    }));
}

function isValidToken(t) {
  if (!/[A-Za-z0-9]/.test(t)) return false;
  const alnum = (t.match(/[A-Za-z0-9\u00C0-\u024F]/g) || []).length;
  if (alnum / t.length < 0.6) return false;
  const hasLetter = /[A-Za-z\u00C0-\u024F]/.test(t);
  const hasDigit = /\d/.test(t);
  if (hasLetter && hasDigit && !/^\d+(st|nd|rd|th)$/i.test(t)) return false;
  if (/(.)\1{3,}/.test(t)) return false;
  if (/[a-z][A-Z]/.test(t)) return false;
  return true;
}

const CONFUSABLE_DIGITS = { "0": "O", "1": "I", "5": "S", "8": "B" };

function evaluateWord(w, minH) {
  const isPunctRaw = PUNCT_ONLY_RE.test(w.text.trim());
  const minConf = isPunctRaw ? 20 : OCR_MIN_CONFIDENCE;
  if (w.confidence < minConf) return { reason: `low confidence (${Math.round(w.confidence)} < ${minConf})` };

  let t = w.text.replace(LEAD_JUNK_RE, "").replace(TRAIL_JUNK_RE, "");
  if (!t) return { reason: "only junk characters" };
  const isPunct = PUNCT_ONLY_RE.test(t);

  if (!isPunct) {
    const digits = (t.match(/\d/g) || []).length;
    const letters = (t.match(/[A-Za-z]/g) || []).length;
    if (digits === 1 && letters >= 2 && !/^\d+(st|nd|rd|th)$/i.test(t)) {
      const fixed = t.replace(/\d/g, (d) => CONFUSABLE_DIGITS[d] || d);
      if (/^([A-Z]+|[a-z]+|[A-Z][a-z]+)$/.test(fixed)) t = fixed;
    }
    if (!isValidToken(t)) return { reason: `invalid token shape ("${t}")` };
    if (w.px.y1 - w.px.y0 < minH) return { reason: `text too small (${Math.round(w.px.y1 - w.px.y0)}px)` };
  }
  return { word: { ...w, text: t, rawText: w.text, isPunct } };
}

function filterLayerWords(words, minH) {
  return words.map((w) => evaluateWord(w, minH).word).filter(Boolean);
}

function unionPx(ws) {
  if (!ws.length) return null;
  return {
    x0: Math.min(...ws.map((w) => w.px.x0)),
    y0: Math.min(...ws.map((w) => w.px.y0)),
    x1: Math.max(...ws.map((w) => w.px.x1)),
    y1: Math.max(...ws.map((w) => w.px.y1)),
  };
}

function wordsConnect(a, b) {
  const ha = a.px.y1 - a.px.y0;
  const hb = b.px.y1 - b.px.y0;
  if (a.isPunct || b.isPunct) {
    const p = a.isPunct ? a : b;
    const o = a.isPunct ? b : a;
    const cy = (p.px.y0 + p.px.y1) / 2;
    if (cy < o.px.y0 || cy > o.px.y1) return false;
  } else {
    const minH = Math.min(ha, hb);
    if (Math.max(ha, hb) / minH > 2.2) return false;
    const ov = Math.min(a.px.y1, b.px.y1) - Math.max(a.px.y0, b.px.y0);
    if (ov / minH < 0.5) return false;
  }
  const gap = Math.max(a.px.x0, b.px.x0) - Math.min(a.px.x1, b.px.x1);
  return gap <= OCR_CONFIG.wordGapRatio * Math.max(ha, hb);
}

function wordsToRegions(words) {
  const parent = words.map((_, i) => i);
  const find = (i) => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  for (let i = 0; i < words.length; i += 1) {
    for (let j = i + 1; j < words.length; j += 1) {
      if (wordsConnect(words[i], words[j])) parent[find(i)] = find(j);
    }
  }
  const buckets = new Map();
  words.forEach((w, i) => {
    const k = find(i);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(w);
  });

  const regions = [];
  buckets.forEach((ws) => {
    ws.sort((a, b) => a.px.x0 - b.px.x0);
    const solid = ws.filter((w) => !w.isPunct);
    if (!solid.length) return;
    const h = median(solid.map((w) => w.px.y1 - w.px.y0));
    let text = ws[0].text;
    for (let i = 1; i < ws.length; i += 1) {
      const gap = ws[i].px.x0 - ws[i - 1].px.x1;
      text += (gap < h * OCR_CONFIG.noSpaceGapRatio ? "" : " ") + ws[i].text;
    }
    const wsum = solid.reduce((s, w) => s + w.text.length, 0) || 1;
    const confidence = solid.reduce((s, w) => s + w.confidence * w.text.length, 0) / wsum;
    const px = {
      x0: Math.min(...ws.map((w) => w.px.x0)),
      y0: Math.min(...ws.map((w) => w.px.y0)),
      x1: Math.max(...ws.map((w) => w.px.x1)),
      y1: Math.max(...ws.map((w) => w.px.y1)),
    };
    const len = Math.max(1, text.replace(/\s/g, "").length);
    regions.push({ text, confidence, px, h, words: ws, score: confidence * Math.sqrt(len) });
  });
  return regions;
}

function layerTextRejectReason(r, minH) {
  const t = r.text;
  const letters = (t.match(/[A-Za-z]/g) || []).length;
  const alnum = (t.match(/[A-Za-z0-9]/g) || []).length;
  const nonSpace = t.replace(/\s/g, "").length;
  const multi = r.words.filter((w) => !w.isPunct).length > 1;

  if (letters === 0) return "no letters (numbers / punctuation only)";
  if (nonSpace === 0 || alnum / nonSpace < 0.6) return "mostly symbols";
  if (r.confidence < OCR_MIN_CONFIDENCE) return `low confidence (${Math.round(r.confidence)})`;
  if (r.h < minH) return `text too small (${Math.round(r.h)}px)`;

  const width = r.px.x1 - r.px.x0;
  const height = r.px.y1 - r.px.y0;
  const aspect = width / Math.max(1, height);
  if (aspect < 0.12 || aspect > 60) return `implausible box shape (aspect ${aspect.toFixed(2)})`;
  if (nonSpace >= 3) {
    const perChar = width / Math.max(1, t.length) / Math.max(1, r.h);
    if (perChar < 0.22 || perChar > 2.2) return `implausible glyph width (${perChar.toFixed(2)})`;
  }

  if (!multi) {
    const word = t.replace(/[^A-Za-z]/g, "");
    if (letters === 1) {
      const weak = /[a-z]/.test(word) && word !== "a" && word !== "i";
      const need = weak ? 80 : 65;
      return r.confidence >= need ? null : `single letter needs confidence ≥ ${need}`;
    }
    if (word.length <= 3 && r.confidence < 60 && !COMMON_SHORT_WORDS.has(word.toLowerCase()))
      return "short word with weak confidence";
    if (word.length >= 3 && !/[aeiouyAEIOUY]/.test(word) && word !== word.toUpperCase())
      return "no vowels (looks like noise)";
  }
  return null;
}

// ---- candidate quality / agreement / geometry (general rules, no word lists) -
const COMMON_BIGRAMS = new Set((
  "th he in er an re on at en nd ti es or te of ed is it al ar st to nt ng se ha as ou io le ve co me de hi ri ro ic ne ea ra ce li ch " +
  "ll be ma si om ur ca el ta la ns di fo ho pe ec pr no ct us ac ot il tr ly nc et ut ss so rs un lo wa ge ie wh ee wi em ad ol rt po " +
  "we na ul ni ts mo ow pa im mi ai sh ir su id os iv ia am fi ld ay ey ob ok ke ki ft ab ag ap bl br bo ck cl cr da dr du ef eg ev ex " +
  "fa fe fl fr ga gi gl go gr gu hu hy ib ig ip iz ja jo ju ka kn lu mp mu my nk ny oa od og oi op oy pi pl pu qu rd rk rn ry sa sc sk " +
  "sl sm sn sp sw sy tu tw ty ud ug um up uc ub ue ui ua ye yo ew aw ph gh ht tc rm rb rg rp rl lt lk lf lp lm lb nf nh nu nw fu ci " +
  "cu cy cc dd ff gg mm nn pp rr tt ls xt xp ds ws ks ps ys gs bs ms hs ix ax ox ez uz"
).split(" "));

const normWord = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");

function wordQuality(text) {
  const tokens = String(text || "").split(/[^A-Za-z]+/).filter(Boolean);
  if (!tokens.length) return { score: 0, flags: ["no letters"] };
  let worst = { score: 1, flags: [] };
  tokens.forEach((tok) => {
    const w = tok.toLowerCase();
    let score = 1;
    const flags = [];
    if (w.length <= 3) {
      if (w.length >= 2 && !/[aeiouy]/.test(w) && tok !== tok.toUpperCase() && !COMMON_SHORT_WORDS.has(w)) {
        score -= 0.4;
        flags.push("short, no vowel");
      }
    } else {
      const pairs = w.length - 1;
      let odd = 0;
      for (let i = 0; i < pairs; i += 1) if (!COMMON_BIGRAMS.has(w.slice(i, i + 2))) odd += 1;
      const frac = odd / pairs;
      if (frac > 0.25) {
        score -= Math.min(0.5, frac * 1.5);
        flags.push(`unusual letter pairs ${Math.round(frac * 100)}%`);
      }
      if (/j[^aeiou]|[^aeiou]j|q[^u]|vf|fv|wq|kq|zx|[bcdfghjkmnpqstvwxz]x/.test(w)) {
        score -= 0.4;
        flags.push("impossible letter pair");
      }
      if (/[^aeiouy]{4,}/.test(w)) {
        score -= 0.3;
        flags.push("long consonant cluster");
      }
      if (/(.)\1{2,}/.test(w)) {
        score -= 0.3;
        flags.push("repeated characters");
      }
      const vr = (w.match(/[aeiouy]/g) || []).length / w.length;
      if (w.length >= 5 && (vr < 0.2 || vr > 0.75)) {
        score -= 0.2;
        flags.push(`odd vowel ratio ${vr.toFixed(2)}`);
      }
      if (w.length > 13) {
        score -= 0.3;
        flags.push(`very long token (${w.length})`);
      } else if (w.length > 10) {
        score -= 0.15;
        flags.push(`long token (${w.length})`);
      }
    }
    if (score < worst.score) worst = { score, flags };
  });
  return { score: Math.max(0, worst.score), flags: worst.flags };
}

function similarity(a, b) {
  if (!a || !b) return 0;
  const m = a.length;
  const n = b.length;
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 1; j <= n; j += 1) d[0][j] = j;
  for (let i = 1; i <= m; i += 1)
    for (let j = 1; j <= n; j += 1)
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return 1 - d[m][n] / Math.max(m, n);
}

function ocrAgreement(passLog, finalText) {
  const f = normWord(finalText);
  const ran = passLog.filter((p) => !p.error && p.psm !== undefined);
  const votes = ran.map((p) => {
    const texts = [p.candidate, ...(p.rawText ? [p.rawText, ...String(p.rawText).split(/\s+/)] : [])].filter(Boolean);
    const best = Math.max(0, ...texts.map((t) => similarity(normWord(t), f)));
    return { label: p.label, agrees: best >= 0.8 };
  });
  const agree = votes.filter((v) => v.agrees).length;
  return { agree, ran: ran.length, score: ran.length ? agree / ran.length : 0 };
}

function geometryCheck(best, region, ppu) {
  const pad = OCR_CONFIG.canvasPad;
  const rw = region.width * ppu;
  const rh = region.height * ppu;
  const w = best.px.x1 - best.px.x0;
  const h = best.px.y1 - best.px.y0;
  const problems = [];
  if (h < OCR_CONFIG.minTextHeightPx) problems.push(`text height ${Math.round(h)}px below floor`);
  if (h / rh > 0.35) problems.push(`label is ${Math.round((h / rh) * 100)}% of layer height`);
  if (w / rw > 0.95) problems.push(`label spans ${Math.round((w / rw) * 100)}% of layer width`);
  if (best.px.x0 < pad - 2 || best.px.y0 < pad - 2 || best.px.x1 > pad + rw + 2 || best.px.y1 > pad + rh + 2)
    problems.push("box outside the layer region");
  return { ok: !problems.length, problems };
}

function judgeCandidate(best, passLog, region, ppu) {
  const q = wordQuality(best.text);
  const ag = ocrAgreement(passLog, best.text);
  const g = geometryCheck(best, region, ppu);
  const reasons = [];
  if (q.score < QUALITY_MIN) reasons.push(`quality too low (${q.score.toFixed(2)}: ${q.flags.join(", ")})`);
  if (ag.ran >= 2 && ag.score < 0.5) reasons.push(`low OCR agreement (${ag.agree}/${ag.ran})`);
  if (ag.agree < 2 && !(best.confidence >= 70 && q.score >= 0.8))
    reasons.push("other passes do not support it and confidence/quality is not strong enough alone");
  if (!g.ok) reasons.push(...g.problems);
  return { accepted: !reasons.length, reasons, quality: q.score, agreement: `${ag.agree}/${ag.ran}` };
}

async function recognizeWords(worker, canvas) {
  const { data } = await worker.recognize(canvas, {}, { blocks: true });
  return collectWords(data);
}

// ---- label colour / extent analysis (only used once translation is enabled) --
function analyzeLabel(ctx, cw, ch, px, h, readable) {
  const fallback = { bg: "rgb(255,255,255)", fg: "rgb(34,34,34)", uniform: true, box: { ...px } };
  if (!readable || !ctx) return fallback;
  try {
    const clampX = (v) => Math.max(0, Math.min(cw, v));
    const clampY = (v) => Math.max(0, Math.min(ch, v));
    const read = (x, y, w, hh) => {
      const ax = Math.round(clampX(x));
      const ay = Math.round(clampY(y));
      const bx = Math.round(clampX(x + w));
      const by = Math.round(clampY(y + hh));
      if (bx <= ax || by <= ay) return null;
      return ctx.getImageData(ax, ay, bx - ax, by - ay).data;
    };

    const infl = Math.round(h * 0.35);
    const th = Math.max(2, Math.round(h * 0.12));
    const x0 = Math.round(px.x0) - infl;
    const y0 = Math.round(px.y0) - infl;
    const x1 = Math.round(px.x1) + infl;
    const y1 = Math.round(px.y1) + infl;
    const strips = [
      [x0 - th, y0 - th, x1 - x0 + 2 * th, th],
      [x0 - th, y1, x1 - x0 + 2 * th, th],
      [x0 - th, y0, th, y1 - y0],
      [x1, y0, th, y1 - y0],
    ];
    const ch3 = [[], [], []];
    strips.forEach((s) => {
      const d = read(...s);
      if (!d) return;
      for (let i = 0; i < d.length; i += 4) {
        ch3[0].push(d[i]);
        ch3[1].push(d[i + 1]);
        ch3[2].push(d[i + 2]);
      }
    });
    if (ch3[0].length < 8) return fallback;
    const med = (arr) => [...arr].sort((a, b) => a - b)[Math.floor(arr.length / 2)];
    const std = (arr) => {
      const m = arr.reduce((s, v) => s + v, 0) / arr.length;
      return Math.sqrt(arr.reduce((s, v) => s + (v - m) * (v - m), 0) / arr.length);
    };
    const bg = ch3.map(med);
    const uniform = (std(ch3[0]) + std(ch3[1]) + std(ch3[2])) / 3 <= OCR_CONFIG.uniformStdMax;
    const bgCss = `rgb(${bg.join(",")})`;

    let fg = [34, 34, 34];
    const d = read(px.x0, px.y0, px.x1 - px.x0, px.y1 - px.y0);
    if (d) {
      const n = d.length / 4;
      const dist = new Float32Array(n);
      for (let i = 0; i < n; i += 1) {
        dist[i] = Math.hypot(d[i * 4] - bg[0], d[i * 4 + 1] - bg[1], d[i * 4 + 2] - bg[2]);
      }
      const thr = Float32Array.from(dist).sort()[Math.floor(n * 0.9)];
      const lum = 0.299 * bg[0] + 0.587 * bg[1] + 0.114 * bg[2];
      if (thr < 60) {
        fg = lum > 140 ? [34, 34, 34] : [255, 255, 255];
      } else {
        let r = 0;
        let g = 0;
        let b = 0;
        let c = 0;
        for (let i = 0; i < n; i += 1) {
          if (dist[i] >= thr) {
            r += d[i * 4];
            g += d[i * 4 + 1];
            b += d[i * 4 + 2];
            c += 1;
          }
        }
        if (c) fg = [Math.round(r / c), Math.round(g / c), Math.round(b / c)];
      }
    }

    const box = { ...px };
    if (uniform && OCR_CONFIG.refineExtents) {
      const inkFrac = (x, y, w, hh) => {
        const dd = read(x, y, w, hh);
        if (!dd) return 0;
        let ink = 0;
        for (let i = 0; i < dd.length; i += 4) {
          if (Math.hypot(dd[i] - bg[0], dd[i + 1] - bg[1], dd[i + 2] - bg[2]) > 70) ink += 1;
        }
        return ink / (dd.length / 4);
      };
      const step = Math.max(2, Math.round(h * 0.08));
      const grow = (side, limit) => {
        let used = 0;
        while (used < limit) {
          const a = Math.round(box.x0);
          const b = Math.round(box.y0);
          const c = Math.round(box.x1);
          const e = Math.round(box.y1);
          let frac = 0;
          if (side === "l") frac = inkFrac(a - step, b, step, e - b);
          if (side === "r") frac = inkFrac(c, b, step, e - b);
          if (side === "t") frac = inkFrac(a, b - step, c - a, step);
          if (side === "b") frac = inkFrac(a, e, c - a, step);
          if (frac < OCR_CONFIG.refineInkFraction) break;
          if (side === "l") box.x0 -= step;
          if (side === "r") box.x1 += step;
          if (side === "t") box.y0 -= step;
          if (side === "b") box.y1 += step;
          used += step;
        }
      };
      const maxX = Math.round(h * OCR_CONFIG.refineMaxXRatio);
      const maxY = Math.round(h * OCR_CONFIG.refineMaxYRatio);
      grow("l", maxX);
      grow("r", maxX);
      grow("t", maxY);
      grow("b", maxY);
    }
    return { bg: bgCss, fg: `rgb(${fg.join(",")})`, uniform, box };
  } catch {
    return fallback;
  }
}

// ---- OCR of one layer canvas -------------------------------------------------
async function ocrLayerCanvas(worker, canvas, cx, tag) {
  const minH = OCR_CONFIG.minTextHeightPx;
  const found = [];
  for (const psm of OCR_CONFIG.psms) {
    const label = `PSM ${psm}${tag}`;
    if ((ocrPsmFailures.get(psm) || 0) >= OCR_CONFIG.psmMaxFailures) {
      cx.passLog.push({ label, error: "skipped (failed too many times)" });
      continue;
    }
    cx.stats.attempted += 1;
    try {
      await worker.setParameters({ tessedit_pageseg_mode: String(psm) });
      const raw = await recognizeWords(worker, canvas);
      const evals = raw.map((w) => ({ w, e: evaluateWord(w, minH) }));
      const words = evals.filter((x) => x.e.word).map((x) => x.e.word);
      const regs = wordsToRegions(words)
        .map((r) => ({ r, reason: layerTextRejectReason(r, minH) }))
        .sort((a, b) => b.r.score - a.r.score);
      const top = regs.filter((x) => !x.reason).map((x) => x.r)[0] || null;
      cx.stats.okPasses += 1;

      const entry = {
        label,
        psm,
        rawText: raw.map((w) => w.text).join(" "),
        rawConf: raw.length ? Math.round(Math.max(...raw.map((w) => w.confidence))) : 0,
        rawPx: unionPx(raw),
        candidate: top ? top.text : null,
        candConf: top ? Math.round(top.confidence) : null,
        reason: null,
      };
      if (!top) {
        entry.reason = !raw.length
          ? "Tesseract returned no words"
          : regs.length
            ? regs[0].reason
            : `all words filtered: ${evals
              .filter((x) => x.e.reason)
              .slice(0, 4)
              .map((x) => `"${x.w.text}" ${x.e.reason}`)
              .join("; ")}`;
      }
      cx.passLog.push(entry);

      if (top) {
        top.psm = psm;
        top.rawText = top.words.map((w) => w.rawText ?? w.text).join(" ");
        found.push(top);
        if (!OCR_CONFIG.debugRunAllPasses && top.confidence >= OCR_CONFIG.earlyExitConfidence) break;
      }
    } catch (err) {
      ocrPsmFailures.set(psm, (ocrPsmFailures.get(psm) || 0) + 1);
      cx.lastErr = err;
      cx.passLog.push({ label, error: err?.message || String(err) });
    }
  }
  if (!found.length) return null;
  const rank = (r) => r.score * Math.pow(wordQuality(r.text).score, 2);
  found.sort((a, b) => rank(b) - rank(a));
  return found[0];
}

// Why an empty layer may be unreadable: structure, clipping, and rendered ink.
function diagnoseEmptyLayer(el, canvas, region, ppu) {
  const tagCount = {};
  el.querySelectorAll("*").forEach((n) => {
    tagCount[n.tagName] = (tagCount[n.tagName] || 0) + 1;
  });
  const out = [
    `content: ${Object.entries(tagCount).map(([t, c]) => `${c}×${t}`).join(", ") || "(empty)"}`,
    `canvas ${canvas.width}×${canvas.height}px @ ${ppu.toFixed(2)} px/unit`,
  ];
  const fx = [];
  for (let n = el; n && n.nodeType === 1; n = n.parentElement) {
    const cs = getComputedStyle(n);
    if (n.getAttribute("clip-path") || (cs.clipPath && cs.clipPath !== "none")) fx.push("clip-path");
    if (n.getAttribute("mask") || (cs.mask && cs.mask !== "none")) fx.push("mask");
    if (parseFloat(cs.opacity) < 1) fx.push(`opacity ${cs.opacity}`);
    if (n.tagName.toLowerCase() === "svg") break;
  }
  if (fx.length) out.push(`effects: ${fx.join(", ")}`);

  if (!canReadCanvasPixels(canvas)) {
    out.push("pixels unreadable (tainted canvas)");
    return out;
  }
  const sw = Math.min(400, canvas.width);
  const sh = Math.max(1, Math.round((canvas.height * sw) / canvas.width));
  const c2 = document.createElement("canvas");
  c2.width = sw;
  c2.height = sh;
  const x2 = c2.getContext("2d", { willReadFrequently: true });
  x2.drawImage(canvas, 0, 0, sw, sh);
  const d = x2.getImageData(0, 0, sw, sh).data;
  let ink = 0;
  for (let i = 0; i < d.length; i += 4) {
    if (Math.hypot(d[i] - d[0], d[i + 1] - d[1], d[i + 2] - d[2]) > 40) ink += 1;
  }
  const pct = (ink / (sw * sh)) * 100;
  out.push(`ink ${pct.toFixed(2)}%${ink === 0 ? " — layer rendered blank (clipped / outside region / invisible)" : ""}`);
  return out;
}

function layerPpu(cx, region) {
  const longUnits = Math.max(region.width, region.height);
  let ppu = cx.dispScale * OCR_CONFIG.ocrScale;
  ppu = Math.min(ppu, OCR_CONFIG.maxLayerSide / longUnits);
  return Math.max(ppu, Math.min(OCR_CONFIG.minLayerSide / longUnits, OCR_CONFIG.maxLayerSide / longUnits));
}

// Real SVG <text> inside the layer (or sharing its id): used directly, no OCR.
function getLayerSvgText(svgEl, vb, layerEl, groupId) {
  const cands = new Set(layerEl.querySelectorAll("text"));
  svgEl.querySelectorAll("text[id]").forEach((t) => {
    if (t.id === groupId) cands.add(t);
  });
  const found = [];
  cands.forEach((t) => {
    if (t.closest(".lsvg-ocr-overlay")) return;
    if (t.closest("defs, clipPath, mask, symbol, pattern, marker")) return;
    const text = (t.textContent || "").replace(/\s+/g, " ").trim();
    if (!text || !/^[\x20-\x7E]+$/.test(text) || !/[A-Za-z]/.test(text)) return;
    try {
      const cs = getComputedStyle(t);
      if (cs.display === "none" || cs.visibility === "hidden" || parseFloat(cs.opacity) === 0) return;
    } catch {
      /* ignore */
    }
    const cr = t.getBoundingClientRect();
    if (!cr.width || !cr.height) return;
    const rect = clientRectToViewBox(svgEl, vb, cr);
    if (!rect || !(rect.width > 0) || !(rect.height > 0)) return;
    found.push({ text, rect });
  });
  if (!found.length) return null;
  found.sort((a, b) => b.rect.width * b.rect.height - a.rect.width * a.rect.height);
  return found[0];
}

// Re-read just the found label from a tight, upscaled crop (single-word PSM).
async function refineBestText(cx, best) {
  const psm = OCR_CONFIG.refinePsm;
  if ((ocrPsmFailures.get(psm) || 0) >= OCR_CONFIG.psmMaxFailures) return;
  const srcCanvas = best.canvas;
  if (!srcCanvas) return;
  const h = best.h;
  const cpad = Math.round(h * 0.6);
  const x0 = Math.max(0, Math.floor(best.px.x0 - cpad));
  const y0 = Math.max(0, Math.floor(best.px.y0 - cpad));
  const x1 = Math.min(srcCanvas.width, Math.ceil(best.px.x1 + cpad));
  const y1 = Math.min(srcCanvas.height, Math.ceil(best.px.y1 + cpad));
  const w = x1 - x0;
  const hh = y1 - y0;
  if (w < 4 || hh < 4) return;

  const up = Math.max(1, Math.min(3, OCR_CONFIG.refineTargetTextPx / Math.max(1, h)));
  const m = OCR_CONFIG.refineMargin;
  const c = document.createElement("canvas");
  c.width = Math.round(w * up) + 2 * m;
  c.height = Math.round(hh * up) + 2 * m;
  const ctx = c.getContext("2d", { willReadFrequently: true });

  let fill = "#ffffff";
  try {
    const sctx = srcCanvas.getContext("2d", { willReadFrequently: true });
    const acc = [0, 0, 0];
    [[x0, y0], [x1 - 1, y0], [x0, y1 - 1], [x1 - 1, y1 - 1]].forEach(([px, py]) => {
      const d = sctx.getImageData(px, py, 1, 1).data;
      acc[0] += d[0];
      acc[1] += d[1];
      acc[2] += d[2];
    });
    fill = `rgb(${Math.round(acc[0] / 4)},${Math.round(acc[1] / 4)},${Math.round(acc[2] / 4)})`;
  } catch {
    /* keep white */
  }
  ctx.fillStyle = fill;
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(srcCanvas, x0, y0, w, hh, m, m, Math.round(w * up), Math.round(hh * up));

  try {
    cx.stats.attempted += 1;
    await cx.worker.setParameters({ tessedit_pageseg_mode: String(psm) });
    const raw = await recognizeWords(cx.worker, c);
    cx.stats.okPasses += 1;
    const mapped = raw.map((wd) => ({
      ...wd,
      px: {
        x0: x0 + (wd.px.x0 - m) / up,
        y0: y0 + (wd.px.y0 - m) / up,
        x1: x0 + (wd.px.x1 - m) / up,
        y1: y0 + (wd.px.y1 - m) / up,
      },
    }));
    const minH = OCR_CONFIG.minTextHeightPx;
    const regs = wordsToRegions(filterLayerWords(mapped, minH))
      .map((r) => ({ r, reason: layerTextRejectReason(r, minH) }))
      .sort((a, b) => b.r.score - a.r.score);
    const top = regs.filter((x) => !x.reason).map((x) => x.r)[0];
    cx.passLog.push({
      label: `PSM ${psm} (tight crop)`,
      psm,
      rawText: raw.map((wd) => wd.text).join(" "),
      rawConf: raw.length ? Math.round(Math.max(...raw.map((wd) => wd.confidence))) : 0,
      candidate: top ? top.text : null,
      candConf: top ? Math.round(top.confidence) : null,
      reason: top ? null : !raw.length ? "Tesseract returned no words" : regs[0]?.reason || "all words filtered",
    });
    if (top && top.confidence >= best.confidence - 5) {
      best.text = top.text;
      best.confidence = top.confidence;
    }
  } catch (err) {
    ocrPsmFailures.set(psm, (ocrPsmFailures.get(psm) || 0) + 1);
    cx.passLog.push({ label: `PSM ${psm} (tight crop)`, error: err?.message || String(err) });
  }
}

// Builds the cacheable layer record (everything in viewBox units).
async function finalizeLayerRecord(cx, item, idx, region, ppu, t) {
  const pad = OCR_CONFIG.canvasPad;
  let analysis;
  if (!OCR_CONFIG.retrievalOnly) {
    try {
      const scene = await renderRegionCanvas(cx.state, region, ppu, pad, { groupId: null, bg: OCR_CONFIG.lightBg });
      if (scene) {
        const sctx = scene.canvas.getContext("2d", { willReadFrequently: true });
        analysis = analyzeLabel(sctx, scene.canvas.width, scene.canvas.height, t.px, t.h, scene.readable);
      }
    } catch {
      /* use default */
    }
  }
  if (!analysis) analysis = analyzeLabel(null, 0, 0, t.px, t.h, false);

  const padX = t.h * (analysis.uniform ? OCR_CONFIG.coverPadXRatio : OCR_CONFIG.coverPadXRatioArt);
  const padY = t.h * (analysis.uniform ? OCR_CONFIG.coverPadYRatio : OCR_CONFIG.coverPadYRatioArt);
  const coverPx = {
    x0: Math.max(pad, analysis.box.x0 - padX),
    y0: Math.max(pad, analysis.box.y0 - padY),
    x1: Math.min(pad + region.width * ppu, analysis.box.x1 + padX),
    y1: Math.min(pad + region.height * ppu, analysis.box.y1 + padY),
  };

  const toVb = (b) => ({
    x: region.x + (b.x0 - pad) / ppu,
    y: region.y + (b.y0 - pad) / ppu,
    width: (b.x1 - b.x0) / ppu,
    height: (b.y1 - b.y0) / ppu,
  });

  return {
    layerId: item.groupId,
    index: idx,
    layer: item.index,
    originalText: t.text,
    text: t.text,
    confidence: Math.round(t.confidence),
    psm: t.psm,
    source: t.source,
    type: t.type,
    region,
    bbox: toVb(t.px),
    cover: toVb(coverPx),
    textHeight: t.h / ppu,
    bg: analysis.bg,
    fg: analysis.fg,
    uniform: analysis.uniform,
    words: t.words.map((w) => toVb(w.px)),
    translations: { en: t.text },
    debug: {
      source: t.source === "svg-text" ? "SVG_TEXT" : t.source === "hand-text" ? "HAND_TEXT" : "OCR",
      rawText: t.rawText ?? t.text,
      rawConf: Math.round(t.confidence),
      finalCandidate: t.text,
      status: "ACCEPTED",
      reason: null,
      bbox: toVb(t.px),
      passes: t.passes || [],
    },
  };
}

// Text extraction for one reading layer.
async function ocrOneLayer(cx, item, idx) {
  const { svgEl, vb, state } = cx;
  const pad = OCR_CONFIG.canvasPad;
  let layerType = null;
  cx.passLog = [];

  const emptyRecord = (reason, detail = reason, source = "OCR", extra = {}) => ({
    layerId: item.groupId,
    index: idx,
    layer: item.index,
    originalText: null,
    text: null,
    confidence: 0,
    reason,
    type: layerType,
    translations: {},
    debug: {
      source,
      finalCandidate: null,
      status: "REJECTED",
      reason: detail,
      shortReason: cx.passLog.find((p) => p.reason)?.reason || reason,
      passes: cx.passLog.slice(),
      ...extra,
    },
  });

  const el = findLayerElement(svgEl, item.groupId);
  if (!el) return emptyRecord("group not found", "group not found in the live SVG", "NONE");
  layerType = el.querySelector("image, path") ? "image/path artwork" : "other artwork";

  // Priority 1: real <text> in the layer
  const direct = OCR_CONFIG.useSvgText ? getLayerSvgText(svgEl, vb, el, item.groupId) : null;
  if (direct) {
    const r = direct.rect;
    const mg = r.height * 1.5;
    const x0 = Math.max(vb.x, r.x - mg);
    const y0 = Math.max(vb.y, r.y - mg);
    const x1 = Math.min(vb.x + vb.width, r.x + r.width + mg);
    const y1 = Math.min(vb.y + vb.height, r.y + r.height + mg);
    const dRegion = { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
    const dPpu = layerPpu(cx, dRegion);
    const px = {
      x0: pad + (r.x - dRegion.x) * dPpu,
      y0: pad + (r.y - dRegion.y) * dPpu,
      x1: pad + (r.x + r.width - dRegion.x) * dPpu,
      y1: pad + (r.y + r.height - dRegion.y) * dPpu,
    };
    return finalizeLayerRecord(cx, item, idx, dRegion, dPpu, {
      text: direct.text,
      rawText: direct.text,
      confidence: 100,
      psm: null,
      px,
      h: px.y1 - px.y0,
      words: [{ px }],
      source: "svg-text",
      type: "real SVG <text>",
      passes: [],
    });
  }

  // Priority 2: OCR the isolated layer
  const region = getLayerRegion(svgEl, vb, el);
  if (!region) return emptyRecord("no bounds", "layer has no visible bounds (0 × 0 / hidden)");
  const ppu = layerPpu(cx, region);

  if (!cx.worker) cx.worker = await getOcrWorker();

  const attempts = OCR_CONFIG.retryOnDark ? [OCR_CONFIG.lightBg, OCR_CONFIG.darkBg] : [OCR_CONFIG.lightBg];
  let best = null;
  let lastRendered = null;
  for (let ai = 0; ai < attempts.length && !best; ai += 1) {
    const rendered = await renderRegionCanvas(state, region, ppu, pad, { groupId: item.groupId, bg: attempts[ai] });
    if (!rendered) {
      cx.passLog.push({ label: "render", error: "layer could not be isolated in the OCR clone" });
      break;
    }
    lastRendered = rendered;
    if (!rendered.readable) {
      cx.passLog.push({ label: "canvas", error: "tainted canvas (cross-origin images need CORS headers)" });
    }
    best = await ocrLayerCanvas(cx.worker, rendered.canvas, cx, ai === 0 ? "" : " (dark bg)");
    if (best) best.canvas = rendered.canvas;
  }

  if (!best) {
    const why =
      cx.passLog.filter((p) => p.reason || p.error).map((p) => `${p.label}: ${p.reason || p.error}`).join(" | ") ||
      "no text";
    const diagnosis = lastRendered ? diagnoseEmptyLayer(el, lastRendered.canvas, region, ppu) : [];
    return emptyRecord("no text", why, "OCR", { diagnosis });
  }

  if (OCR_CONFIG.refineEnabled) await refineBestText(cx, best);

  const verdict = judgeCandidate(best, cx.passLog, region, ppu);
  if (!verdict.accepted) {
    return emptyRecord("candidate rejected", verdict.reasons.join("; "), "OCR", {
      shortReason: verdict.reasons[0],
      rejectedCandidate: best.text,
      verdict,
    });
  }

  const rec = await finalizeLayerRecord(cx, item, idx, region, ppu, {
    text: best.text,
    rawText: best.rawText,
    confidence: best.confidence,
    psm: best.psm,
    px: best.px,
    h: best.h,
    words: best.words,
    source: "ocr",
    type: layerType,
    passes: cx.passLog.slice(),
  });
  rec.debug.verdict = verdict;
  return rec;
}

// ---- hand-marker text test (read-only; compares with OCR) --------------------
// Run-together values ("whatisyourfavouritefood") are split with a small common-word list.
// Preferred fix: the content team separates words with underscores (hand_text_what_is_your_food).
const SEG_WORDS = new Set((
  "a i am is are was be do does did you your my me we our he she it they his her its their the this that these those " +
  "what who where when why how which and or but if so not no yes can will like likes love want have has had go going come " +
  "eat eating drink play name age old new big small little one two three four five six seven eight nine ten " +
  "favourite favorite food color colour animal fruit fruits vegetable vegetables day night morning today good bad happy sad hot cold " +
  "dosa idli rice milk water tea coffee bread egg eggs apple banana mango orange grapes pineapple cake sweet sweets " +
  "school book home mother father friend teacher boy girl dog cat bird fish see hear taste touch smell " +
  "with in on at of to for from by up down out over under here there very much many some any all more most " +
  "tell say said hello please thank thanks sorry bye"
).split(" "));
const QUESTION_WORDS = new Set("what who where when why how which do does is are can".split(" "));

function segmentRunTogether(s) {
  const w = s.toLowerCase();
  const n = w.length;
  const cost = Array(n + 1).fill(Infinity);
  const back = Array(n + 1).fill(null);
  cost[0] = 0;
  for (let i = 1; i <= n; i += 1) {
    cost[i] = cost[i - 1] + 4;
    back[i] = { len: 1, known: false };
    for (let len = 1; len <= Math.min(14, i); len += 1) {
      if (SEG_WORDS.has(w.slice(i - len, i)) && cost[i - len] + 1 < cost[i]) {
        cost[i] = cost[i - len] + 1;
        back[i] = { len, known: true };
      }
    }
  }
  const pieces = [];
  for (let i = n; i > 0; i -= back[i].len) pieces.unshift({ t: w.slice(i - back[i].len, i), known: back[i].known });
  const merged = [];
  pieces.forEach((p) => {
    const last = merged[merged.length - 1];
    if (!p.known && last && !last.known) last.t += p.t;
    else merged.push({ ...p });
  });
  return { words: merged.map((p) => p.t), unknown: merged.some((p) => !p.known) };
}

const HAND_TEXT_OVERRIDES = {
  whatisyourfavouritefood: "What is your favourite food?",
  ilikedosa: "I like dosa.",
};

function formatSentence(words) {
  if (!words.length) return "";
  let t = words.join(" ").replace(/\bi\b/g, "I");
  t = t.charAt(0).toUpperCase() + t.slice(1);
  if (!/[?.!]$/.test(t)) {
    t += QUESTION_WORDS.has(words[0].toLowerCase()) && words.length > 2 ? "?" : ".";
  }
  return t;
}

// hand_x5F_text_x5F_<value> → decoded "hand_text_<value>" → TEXT marker.
function parseHandMarker(id) {
  const decoded = String(id || "").replace(/_x([0-9A-Fa-f]{2})_/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
  const parts = decoded.split("_");
  if (parts[0] !== "hand" || parts.length < 2) return { type: "UNKNOWN", decoded };
  if (!parts.includes("text")) return { type: "NON-TEXT", decoded };
  if (parts[1] !== "text") return { type: "UNKNOWN", decoded };
  const rest = parts.slice(2);
  if (!rest.join("").trim()) return { type: "UNKNOWN", decoded };
  const last = rest[rest.length - 1];
  const suffix = rest.length > 1 && /^\d{1,3}$/.test(last) ? last : null;
  let words = rest;
  let segmented = false;
  let unknownChunk = false;
  if (rest.length === 1 && rest[0].length > 3 && !SEG_WORDS.has(rest[0].toLowerCase())) {
    const seg = segmentRunTogether(rest[0]);
    if (!seg.unknown && seg.words.length > 1) {
      words = seg.words;
      segmented = true;
    }
  }
  const key = rest.join("").toLowerCase().replace(/[^a-z]/g, "");
  if (HAND_TEXT_OVERRIDES[key]) {
    return {
      type: "TEXT",
      decoded,
      valueRaw: rest.join("_"),
      text: HAND_TEXT_OVERRIDES[key],
      suffix,
      segmented: false,
      unknownChunk: false,
    };
  }

  return { type: "TEXT", decoded, valueRaw: rest.join("_"), text: formatSentence(words), suffix, segmented, unknownChunk };
}

function collectHandInfo(svgEl, vb, markers, order) {
  const readingIds = new Set(order.map((it) => it.groupId));
  const rectOf = (el) => clientRectToViewBox(svgEl, vb, el.getBoundingClientRect());
 
  const info = markers.map((m, i) => {
    // decoding + bbox: same logic as before
    const p = parseHandMarker(m.id);
    const box = rectOf(m);
    const usable =
      !!box && box.width > 0 && box.height > 0 &&
      box.x + box.width > vb.x && box.x < vb.x + vb.width &&
      box.y + box.height > vb.y && box.y < vb.y + vb.height;
 
    // containing layer: nearest ancestor <g> that is a reading layer
    let groupId = null;
    let containerId = null; // nearest ancestor <g id> of any kind (debug only)
    let groupSource = "none";
    for (let n = m.parentElement; n && n !== svgEl; n = n.parentElement) {
      if (n.localName !== "g" || !n.id || n.id.startsWith("hand_x5F_")) continue;
      if (!containerId) containerId = n.id;
      if (readingIds.has(n.id)) {
        groupId = n.id;
        groupSource = "contains marker";
        break;
      }
    }
    // old behaviour kept as a fallback: marker N sits next to reading layer N
    if (!groupId && order[i]) {
      groupId = order[i].groupId;
      groupSource = "paired by order";
    }
    return { ...p, index: i + 1, rawId: m.id, box, usable, groupId, groupSource, containerId };
  });
 
  // a reading layer shared by several markers cannot identify any single one of them
  const perGroup = new Map();
  info.forEach((h) => {
    if (h.groupId) perGroup.set(h.groupId, (perGroup.get(h.groupId) || 0) + 1);
  });
 
  const layerRects = new Map();
  return info.map((h) => {
    let inLayer = null; // null = no layer box to compare with
    if (h.groupId && h.box) {
      if (!layerRects.has(h.groupId)) {
        const el = findLayerElement(svgEl, h.groupId);
        layerRects.set(h.groupId, el ? rectOf(el) : null);
      }
      const lr = layerRects.get(h.groupId);
      if (lr) {
        const mcx = h.box.x + h.box.width / 2;
        const mcy = h.box.y + h.box.height / 2;
        inLayer =
          mcx >= lr.x - lr.width * 0.1 && mcx <= lr.x + lr.width * 1.1 &&
          mcy >= lr.y - lr.height * 0.1 && mcy <= lr.y + lr.height * 1.1;
      }
    }
    return { ...h, inLayer, exclusive: !!h.groupId && perGroup.get(h.groupId) === 1 };
  });
}
async function detectBubble(cx, item, h) {
  const { svgEl, vb, state } = cx;
  const fallback = { shape: "rect", box: h.box, cover: h.box, bg: null, textBox: null };
  const fail = (why) => {
    console.warn(`⚠️ Layer ${item.index}: bubble auto-detect failed (${why}), using the marker box`);
    return fallback;
  };
  if (!OCR_CONFIG.bubbleMode || !state) return fallback;

  try {
    const mx = h.box.x + h.box.width / 2;
    const my = h.box.y + h.box.height / 2;

    // search window: the reading layer's box (only used to limit the area to scan)
    const el = findLayerElement(svgEl, item.groupId);
    let region = el ? getLayerRegion(svgEl, vb, el) : null;
    if (!region || mx < region.x || mx > region.x + region.width || my < region.y || my > region.y + region.height) {
      const w = Math.min(vb.width, vb.width * 0.4);
      const hh = Math.min(vb.height, vb.height * 0.4);
      region = {
        x: Math.max(vb.x, Math.min(mx - w / 2, vb.x + vb.width - w)),
        y: Math.max(vb.y, Math.min(my - hh / 2, vb.y + vb.height - hh)),
        width: w,
        height: hh,
      };
    }

    const ppu = OCR_CONFIG.bubbleSearchPx / Math.max(region.width, region.height);
    const scene = await renderRegionCanvas(state, region, ppu, 0, { groupId: null, bg: OCR_CONFIG.lightBg });
    if (!scene || !scene.readable) return fail("canvas not readable");

    const W = scene.canvas.width;
    const H = scene.canvas.height;
    const px = scene.canvas.getContext("2d", { willReadFrequently: true }).getImageData(0, 0, W, H).data;
    const sx = Math.max(0, Math.min(W - 1, Math.round((mx - region.x) * ppu)));
    const sy = Math.max(0, Math.min(H - 1, Math.round((my - region.y) * ppu)));

    // 1) bubble fill colour = most common colour around the marker centre
    const bins = new Map();
    const rx = Math.max(4, Math.round(W * 0.06));
    const ry = Math.max(4, Math.round(H * 0.06));
    for (let y = Math.max(0, sy - ry); y < Math.min(H, sy + ry); y += 3) {
      for (let x = Math.max(0, sx - rx); x < Math.min(W, sx + rx); x += 3) {
        const i = (y * W + x) * 4;
        const k = (px[i] >> 4) * 256 + (px[i + 1] >> 4) * 16 + (px[i + 2] >> 4);
        const b = bins.get(k) || { n: 0, r: 0, g: 0, b: 0 };
        b.n += 1;
        b.r += px[i];
        b.g += px[i + 1];
        b.b += px[i + 2];
        bins.set(k, b);
      }
    }
    let top = null;
    bins.forEach((b) => {
      if (!top || b.n > top.n) top = b;
    });
    if (!top) return fail("no colour samples");
    const bg = [Math.round(top.r / top.n), Math.round(top.g / top.n), Math.round(top.b / top.n)];

    const tol = OCR_CONFIG.bubbleFillTolerance;
    const isBg = (q) => {
      const i = q * 4;
      return Math.hypot(px[i] - bg[0], px[i + 1] - bg[1], px[i + 2] - bg[2]) <= tol;
    };

    // 2) seed: nearest background-coloured pixel to the marker centre
    let seed = -1;
    const maxR = Math.round(Math.max(W, H) * 0.25);
    for (let r = 0; r <= maxR && seed < 0; r += 4) {
      for (let d = -r; d <= r && seed < 0; d += 4) {
        const cand = [[sx + d, sy - r], [sx + d, sy + r], [sx - r, sy + d], [sx + r, sy + d]];
        for (const [x, y] of cand) {
          if (x >= 0 && y >= 0 && x < W && y < H && isBg(y * W + x)) {
            seed = y * W + x;
            break;
          }
        }
      }
    }
    if (seed < 0) return fail("no background pixel near the marker");

    // 3) flood-fill the bubble interior (the border and tail stop it)
    const seen = new Uint8Array(W * H);
    const stack = [seed];
    seen[seed] = 1;
    const rowMin = new Int32Array(H).fill(W);
    const rowMax = new Int32Array(H).fill(-1);
    let count = 0;
    const tryPush = (q) => {
      if (seen[q]) return;
      seen[q] = 1;
      if (isBg(q)) stack.push(q);
    };
    while (stack.length) {
      const p = stack.pop();
      const x = p % W;
      const y = (p - x) / W;
      count += 1;
      if (x < rowMin[y]) rowMin[y] = x;
      if (x > rowMax[y]) rowMax[y] = x;
      if (x > 0) tryPush(p - 1);
      if (x < W - 1) tryPush(p + 1);
      if (y > 0) tryPush(p - W);
      if (y < H - 1) tryPush(p + W);
    }
    if (count > W * H * 0.8) return fail("fill leaked out of the bubble");

    // 4) interior box = rows that are wide enough (drops the thin tail)
    let maxSpan = 0;
    for (let y = 0; y < H; y += 1) if (rowMax[y] >= 0) maxSpan = Math.max(maxSpan, rowMax[y] - rowMin[y] + 1);
    let x0 = W;
    let x1 = -1;
    let y0 = H;
    let y1 = -1;
    for (let y = 0; y < H; y += 1) {
      if (rowMax[y] < 0 || rowMax[y] - rowMin[y] + 1 < maxSpan * 0.45) continue;
      x0 = Math.min(x0, rowMin[y]);
      x1 = Math.max(x1, rowMax[y]);
      y0 = Math.min(y0, y);
      y1 = Math.max(y1, y);
    }
    if (x1 < 0 || x1 - x0 < W * 0.04 || y1 - y0 < H * 0.04) return fail("interior too small");

    const iv = {
      x: region.x + x0 / ppu,
      y: region.y + y0 / ppu,
      width: (x1 - x0 + 1) / ppu,
      height: (y1 - y0 + 1) / ppu,
    };
    // English text = non-background pixels enclosed by the bubble fill (main interior rows only)
    const rowInk = new Int32Array(H);
    const colInk = new Int32Array(W);
    for (let y = y0; y <= y1; y += 1) {
      if (rowMax[y] < 0) continue;
      for (let x = rowMin[y]; x <= rowMax[y]; x += 1) {
        if (!isBg(y * W + x)) {
          rowInk[y] += 1;
          colInk[x] += 1;
        }
      }
    }
    let ix0 = -1;
    let ix1 = -1;
    let iy0 = -1;
    let iy1 = -1;
    for (let y = y0; y <= y1; y += 1) if (rowInk[y] >= 2) { if (iy0 < 0) iy0 = y; iy1 = y; }
    for (let x = x0; x <= x1; x += 1) if (colInk[x] >= 2) { if (ix0 < 0) ix0 = x; ix1 = x; }
    const textBox =
      ix0 >= 0 && iy0 >= 0
        ? {
          x: region.x + ix0 / ppu,
          y: region.y + iy0 / ppu,
          width: (ix1 - ix0 + 1) / ppu,
          height: (iy1 - iy0 + 1) / ppu,
        }
        : null;
    const icx = iv.x + iv.width / 2;
    const icy = iv.y + iv.height / 2;
    const cs = OCR_CONFIG.bubbleCoverScale;
    const ts = OCR_CONFIG.bubbleTextScale;
    const ellipse = OCR_CONFIG.bubbleShape === "ellipse";

    return {
      shape: ellipse ? "ellipse" : "rect",
      interior: iv,
      textBox,
      cover: ellipse
        ? { cx: icx, cy: icy, rx: (iv.width / 2) * cs, ry: (iv.height / 2) * cs }
        : { x: icx - (iv.width * cs) / 2, y: icy - (iv.height * cs) / 2, width: iv.width * cs, height: iv.height * cs },
      box: { x: icx - (iv.width * ts.w) / 2, y: icy - (iv.height * ts.h) / 2, width: iv.width * ts.w, height: iv.height * ts.h },
      bg: `rgb(${bg.join(",")})`,
    };
  } catch (err) {
    return fail(err?.message || String(err));
  }
}

// Finds the ACTUAL English text of a hand marker. The marker only says WHICH text
// it is (its name carries the words); its own box is where the hand points, not
// where the English is. Returns { rect (viewBox units), source } or null.
async function locateEnglishBox(cx, h) {
  const { svgEl, vb } = cx;
  const target = normWord(h.text);
  if (!target) return null;

  // marker centre: ONLY used to choose between identical words, never as a position
  const mcx = h.box.x + h.box.width / 2;
  const mcy = h.box.y + h.box.height / 2;

  // 1) real <text> elements whose letters equal the marker text
  //    (same getBoundingClientRect -> clientRectToViewBox path as getLayerSvgText)
  const cands = [];
  svgEl.querySelectorAll("text").forEach((tn) => {
    if (tn.closest('.lsvg-ocr-overlay, defs, clipPath, mask, symbol, pattern, marker, [id^="hand_x5F_"]')) return;
    const norm = normWord(tn.textContent);
    if (!norm) return;
    const cr = tn.getBoundingClientRect();
    if (!cr.width || !cr.height) return;
    const rect = clientRectToViewBox(svgEl, vb, cr);
    if (!rect || !(rect.width > 0) || !(rect.height > 0)) return;
    cands.push({ norm, rect });
  });

  let groups = cands.filter((c) => c.norm === target).map((c) => [c]);
  if (!groups.length && target.length >= 4) {
    groups = cands.filter((c) => similarity(c.norm, target) >= 0.85).map((c) => [c]);
  }
  if (!groups.length) {
    // multi-line English stored as several <text> elements in a row
    for (let s = 0; s < cands.length; s += 1) {
      let acc = "";
      for (let e = s; e < cands.length; e += 1) {
        acc += cands[e].norm;
        if (acc === target) {
          if (e > s) groups.push(cands.slice(s, e + 1));
          break;
        }
        if (acc.length >= target.length) break;
      }
    }
  }
  if (groups.length) {
    let best = null;
    let bestD = Infinity;
    let bestRect = null;
    groups.forEach((gp) => {
      const x0 = Math.min(...gp.map((c) => c.rect.x));
      const y0 = Math.min(...gp.map((c) => c.rect.y));
      const x1 = Math.max(...gp.map((c) => c.rect.x + c.rect.width));
      const y1 = Math.max(...gp.map((c) => c.rect.y + c.rect.height));
      const d = Math.hypot((x0 + x1) / 2 - mcx, (y0 + y1) / 2 - mcy);
      if (d < bestD) {
        best = gp;
        bestD = d;
        bestRect = { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
      }
    });
    if (best) return { rect: bestRect, source: "REAL SVG TEXT" };
  }

  // 2) the marker's reading layer, through the EXISTING pipeline:
  //    real <text> in the layer, else OCR of the isolated layer (both return viewBox units).
  //    Accepted only if what it read matches the marker text.
  if (h.groupId) {
    try {
      const rec = await ocrOneLayer(cx, { index: h.index, groupId: h.groupId }, h.index - 1);
      if (rec && rec.text && rec.bbox) {
        if (similarity(normWord(rec.text), target) >= 0.75) {
          return { rect: rec.bbox, source: rec.source === "svg-text" ? "LAYER SVG TEXT" : "LAYER OCR" };
        }
        console.warn(`⚠️ Marker ${h.index}: layer text "${rec.text}" does not match "${h.text}", not used as the anchor`);
      } else {
        console.warn(`⚠️ Marker ${h.index}: no text found in layer ${h.groupId} (${rec?.debug?.reason || "unknown"})`);
      }
    } catch (err) {
      console.warn(`⚠️ Marker ${h.index}: layer text lookup failed: ${err?.message || err}`);
    }
  }
  return null;
}

// Layer record for a hand_text marker. The record's bbox is now the ENGLISH TEXT box.
async function handTextRecord(cx, item, idx, h) {
  const { vb } = cx;
  const pad = OCR_CONFIG.canvasPad;

  // ANCHOR: the real English text box (viewBox units). Marker box only as a last resort.
  let located = null;
  try {
    located = await locateEnglishBox(cx, h);
  } catch (err) {
    console.warn(`⚠️ Marker ${h.index}: English text lookup failed: ${err?.message || err}`);
  }
  const r = located ? located.rect : h.box;
  const bboxSource = located ? located.source : "HAND MARKER FALLBACK";
  console.log(
    `📍 Marker ${h.index} "${h.text}": English bbox source = ${bboxSource} | ` +
    `bbox x=${r.x.toFixed(1)}, y=${r.y.toFixed(1)}, w=${r.width.toFixed(1)}, h=${r.height.toFixed(1)} | ` +
    `marker box x=${h.box.x.toFixed(1)}, y=${h.box.y.toFixed(1)}`
  );

  const mg = r.height * 1.5;
  const x0 = Math.max(vb.x, r.x - mg);
  const y0 = Math.max(vb.y, r.y - mg);
  const x1 = Math.min(vb.x + vb.width, r.x + r.width + mg);
  const y1 = Math.min(vb.y + vb.height, r.y + r.height + mg);
  const region = { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
  const ppu = layerPpu(cx, region);
  const px = {
    x0: pad + (r.x - region.x) * ppu,
    y0: pad + (r.y - region.y) * ppu,
    x1: pad + (r.x + r.width - region.x) * ppu,
    y1: pad + (r.y + r.height - region.y) * ppu,
  };
  const rec = await finalizeLayerRecord(cx, item, idx, region, ppu, {
    text: h.text,
    rawText: h.valueRaw,
    confidence: 100,
    psm: null,
    px,
    h: px.y1 - px.y0,
    words: [{ px }],
    source: "hand-text",
    type: "hand_text marker",
    passes: [],
  });
  rec.bboxSource = bboxSource;
  rec.markerBox = h.box;
  // bubble detection is seeded from the ENGLISH box (not from the marker)
  rec.bubble = await detectBubble(cx, item, { ...h, box: r });
  if (OCR_CONFIG.debugBoxes) {
    const i = rec.bubble.interior;
    console.log(
      `🫧 Layer ${item.index}: bubble ${rec.bubble.shape}` +
      (i ? ` interior ${i.width.toFixed(0)}×${i.height.toFixed(0)} at (${i.x.toFixed(0)}, ${i.y.toFixed(0)})` : " (fallback)")
    );
  }
  return rec;
}

function logHandSummary(order, hand) {
  const f = (b) => (b ? `x=${b.x.toFixed(1)}, y=${b.y.toFixed(1)}, w=${b.width.toFixed(1)}, h=${b.height.toFixed(1)}` : "none");
  const count = (t) => hand.filter((h) => h.type === t).length;
  const boxOk = (h) => h.usable && h.inLayer !== false;
  const badText = hand.filter((h) => h.type === "TEXT" && !h.usable).length;
 
  const lines = [
    `Reading layers: ${order.length}`,
    `Hand markers inspected: ${hand.length}`,
    `TEXT: ${count("TEXT")} | NON-TEXT: ${count("NON-TEXT")} | UNKNOWN: ${count("UNKNOWN")}`,
    "",
  ];
  hand.forEach((h) => {
    lines.push(
      `Marker ${h.index}  layer: ${h.groupId || "none"} (${h.groupSource})${h.groupId && !h.exclusive ? " — shared with other markers" : ""}`,
      `  raw:     ${h.rawId}`,
      `  decoded: ${h.decoded}`,
      `  type:    ${h.type}`
    );
    if (h.type === "TEXT") {
      lines.push(
        `  text:    "${h.text}"${h.valueRaw !== h.text ? `  (raw value "${h.valueRaw}")` : ""}`,
        ...(h.segmented ? ["  ℹ️ value had no separators — split with the common-word list"] : []),
        ...(h.unknownChunk ? ["  ⚠️ part of the value is not in the word list — check the text above"] : []),
        ...(h.suffix ? [`  ⚠️ ends in "_${h.suffix}" — possible export suffix, not stripped`] : [])
      );
    }
    lines.push(
      `  box:     ${f(h.box)}`,
      `  usable:  ${h.usable ? "yes" : "NO"} | inside its layer: ${h.inLayer === null ? "n/a (no layer box)" : h.inLayer ? "yes" : "NO"}`,
      ""
    );
  });
  logBlock("🖐️ HAND TEXT DETECTION", lines, count("UNKNOWN") || badText ? "warn" : "log");
 
  console.table(
    hand.map((h) => ({
      Layer: h.index,
      Marker: h.rawId,
      Type: h.type,
      Text: h.type === "TEXT" ? h.text : "—",
      "Box ok": boxOk(h) ? "yes" : "no",
      Container: h.groupId || h.containerId || "—",
    }))
  );
}

function compareHandOcr(h, rec) {
  if (!h || h.type !== "TEXT") return "N/A";
  const cands = [rec?.text, ...(rec?.debug?.passes || []).flatMap((p) => [p.candidate, p.rawText])].filter(Boolean);
  if (!cands.length) return "OCR NONE";
  const target = normWord(h.text);
  const best = Math.max(...cands.map((c) => similarity(normWord(c), target)));
  return best === 1 ? "MATCH" : best >= 0.85 ? "MATCH (near)" : "DIFFERENT";
}

// ---- summary report (one block + one table) ----------------------------------
function logRetrievalSummary(order, recs, hand) {
  const rows = order.map((item, i) => {
    const rec = recs[i];
    const d = rec?.debug;
    const h = hand[i];
    return {
      Layer: item.index,
      Source: d?.source || "ERROR",
      "OCR word": rec?.text || "—",
      Conf: rec?.text ? rec.confidence : "—",
      "Hand type": h?.type || "—",
      "Hand text": h?.type === "TEXT" ? h.text : "—",
      Match: compareHandOcr(h, rec),
      "Marker box ok": h ? (h.usable && h.inLayer ? "yes" : "no") : "—",
    };
  });

  const lines = [];
  order.forEach((item, i) => {
    const d = recs[i]?.debug;
    if (!d || d.status === "ACCEPTED") return;
    lines.push(
      `Layer ${item.index}: no word — ${d.shortReason || d.reason}` +
      (d.rejectedCandidate ? ` (rejected "${d.rejectedCandidate}")` : "")
    );
    (d.diagnosis || []).forEach((l) => lines.push(`    ${l}`));
  });
  hand.forEach((h, i) => {
    if (h?.type === "TEXT" && h.suffix) {
      lines.push(`Layer ${order[i].index}: hand text ends in "_${h.suffix}" (possible export suffix, not stripped)`);
    }
  });

  const count = (t) => hand.filter((h) => h?.type === t).length;
  const accepted = recs.filter((r) => r?.debug?.status === "ACCEPTED").length;
  const textMarkers = hand.filter((h) => h?.type === "TEXT");

  logBlock(
    "📊 WORD RETRIEVAL SUMMARY",
    [
      `Layers: ${order.length} | OCR words: ${accepted}/${order.length}`,
      `Hand markers — TEXT: ${count("TEXT")}, NON-TEXT: ${count("NON-TEXT")}, UNKNOWN: ${count("UNKNOWN")}, MISSING: ${count("MISSING")}`,
      `TEXT markers with usable box inside their layer: ${textMarkers.filter((h) => h.usable && h.inLayer).length}/${textMarkers.length}`,
      ...(lines.length ? ["", "Details:", ...lines] : []),
    ],
    accepted === order.length ? "log" : "warn"
  );
  console.table(rows);
}

// OCR every reading layer once per `src`; concurrent callers share the promise.
function runLayerOcr(svgEl, src, readingOrder, store) {
  const existing = ocrCache.get(src);
  if (existing) return existing.promise;
 
  const entry = { status: "pending", promise: null, data: null, error: null };
  ocrCache.set(src, entry);
 
  entry.promise = (async () => {
    const vb = getSvgViewBox(svgEl);
    if (!vb) throw new Error("SVG has no usable viewBox / width / height");
 
    const order = readingOrder || [];
 
    // HAND MARKERS: the complete, independent collection (same query the parser used)
    const markers = Array.from(svgEl.querySelectorAll('[id^="hand_x5F_"]'));
    const keyOf = (h) => `${src}::marker${h.index}::${h.rawId}`;
 
    let hand = [];
    try {
      hand = collectHandInfo(svgEl, vb, markers, order);
      logHandSummary(order, hand);
    } catch (err) {
      console.error("Hand marker detection failed", err);
    }
 
    const isHandText = (h) => h.type === "TEXT" && h.usable;
    const todo = hand.filter((h) => isHandText(h) && !store[keyOf(h)]);
 
    const cx = {
      svgEl,
      vb,
      src,
      state: null,
      worker: null,
      dispScale: 1,
      lastErr: null,
      passLog: [],
      stats: { attempted: 0, okPasses: 0 },
    };
 
    if (todo.length) {
      await waitForSvgRendered(svgEl);
      cx.dispScale = (svgEl.getBoundingClientRect().width || vb.width) / vb.width;
      cx.state = await prepareOcrBase(svgEl, vb, src);
      for (const h of todo) {
        // a reading layer shared by several markers is not used as this marker's layer
        const item = { index: h.index, groupId: h.exclusive ? h.groupId : null };
        try {
          store[keyOf(h)] = await handTextRecord(cx, item, h.index - 1, h);
        } catch (err) {
          console.error(`❌ Hand text record failed for marker ${h.index} (${h.rawId}): ${err?.message || err}`);
        }
      }
    }
 
    const textMarkers = hand.filter(isHandText);
    const nonText = hand.filter((h) => h.type === "NON-TEXT");
    const other = hand.filter((h) => h.type === "UNKNOWN" || (h.type === "TEXT" && !h.usable));
    const label = (h) => `${h.index} (${h.rawId})`;
 
    logBlock(
      "🖐️ HAND-TEXT ONLY",
      [
        `Hand markers inspected: ${hand.length}`,
        `Translating layers: ${textMarkers.map(label).join(", ") || "none"}`,
        `Ignored NON-TEXT: ${nonText.map(label).join(", ") || "none"}`,
        `Ignored UNKNOWN / unusable TEXT: ${other.map(label).join(", ") || "none"}`,
      ],
      other.length ? "warn" : "log"
    );
 
    const layers = textMarkers.map((h) => store[keyOf(h)]).filter(Boolean);
 
    return { key: src, vb, layers };
  })()
    .then((data) => {
      entry.status = "done";
      entry.data = data;
      return data;
    })
    .catch((err) => {
      entry.status = "failed";
      entry.error = err;
      throw err;
    });
 
  return entry.promise;
}

// ---- translation (inactive while OCR_CONFIG.retrievalOnly is true) -----------
async function translateText(text, target) {
  const key = `${target}::${text}`;
  if (translationCache.has(key)) return translationCache.get(key);

  const url =
    `${OCR_CONFIG.translateEndpoint}?client=gtx&sl=en&tl=${encodeURIComponent(target)}` +
    `&dt=t&q=${encodeURIComponent(text)}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), OCR_CONFIG.translateTimeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const out = (json?.[0] || []).map((seg) => seg?.[0] || "").join("").trim();
    if (!out) throw new Error("empty translation");
    translationCache.set(key, out);
    return out;
  } finally {
    clearTimeout(timer);
  }
}

function getEnglishTextRect(svgEl, vb, layerId) {
  const el = findLayerElement(svgEl, layerId);
  if (!el) return null;
  const primary = getLayerSvgText(svgEl, vb, el, layerId);
  if (!primary) return null;
 
  let x0 = primary.rect.x;
  let y0 = primary.rect.y;
  let x1 = primary.rect.x + primary.rect.width;
  let y1 = primary.rect.y + primary.rect.height;
 
  // multi-line English is often several <text> elements: include them all
  el.querySelectorAll("text").forEach((tn) => {
    if (tn.closest(".lsvg-ocr-overlay")) return;
    if (tn.closest("defs, clipPath, mask, symbol, pattern, marker")) return;
    const s = (tn.textContent || "").replace(/\s+/g, " ").trim();
    if (!s || !/^[\x20-\x7E]+$/.test(s) || !/[A-Za-z]/.test(s)) return;
    const cr = tn.getBoundingClientRect();
    if (!cr.width || !cr.height) return;
    const rc = clientRectToViewBox(svgEl, vb, cr);
    if (!rc || !(rc.width > 0) || !(rc.height > 0)) return;
    x0 = Math.min(x0, rc.x);
    y0 = Math.min(y0, rc.y);
    x1 = Math.max(x1, rc.x + rc.width);
    y1 = Math.max(y1, rc.y + rc.height);
  });
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}
 
const TRANSLATION_FONT_SIZE = 12; // fixed, never shrunk

// Simple callout: translation bubble placed from the ENGLISH text bbox, tail tip at its centre.
function buildTranslationOverlay(svgEl, results, translations, vbArg) {
  const C = OCR_CONFIG;
  const vb = vbArg || getSvgViewBox(svgEl);
  if (!vb) return;

  const FS = TRANSLATION_FONT_SIZE;
  const LINE_H = FS * 1.25;
  const PAD_X = 12;
  const PAD_Y = 8;
  const RADIUS = 8;
  const GAP = 8; // space between bubble edge and the English bbox
  const TAIL_HALF = 8; // half width of the tail base
  const MAX_LINES = 3;
  const MAX_LINE_W = vb.width * 0.4;
  const f1 = (n) => (Number.isFinite(n) ? n.toFixed(1) : String(n));
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("class", "lsvg-ocr-overlay notranslate");
  g.setAttribute("translate", "no");
  g.setAttribute("pointer-events", "none");
  g.style.pointerEvents = "none";
  svgEl.appendChild(g);

  // English boxes for every layer first, so bubbles can avoid each other and other English text
  const engInfo = results.map((r) => {
    if (r.bboxSource && r.bboxSource !== "HAND MARKER FALLBACK") return { eng: r.bbox, source: r.bboxSource };
    const real = getEnglishTextRect(svgEl, vb, r.layerId);
    if (real) return { eng: real, source: "REAL SVG TEXT" };
    if (r.bubble && r.bubble.textBox) return { eng: r.bubble.textBox, source: "BUBBLE TEXT PIXELS" };
    return { eng: r.bbox, source: "HAND MARKER FALLBACK" };
  });
  const placed = []; // bubble rects already drawn
  const overlapArea = (a, b) => {
    const w = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
    const h = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
    return w > 0 && h > 0 ? w * h : 0;
  };

  results.forEach((r, i) => {
    const tr = translations[i];
    if (!tr || tr.trim().toLowerCase() === r.text.trim().toLowerCase()) return;

    // 1) English bbox (computed up front for all layers, see engInfo)
    const eng = engInfo[i].eng;
    const source = engInfo[i].source;

    // 2) target point = centre of the English text
    const targetX = eng.x + eng.width / 2;
    const targetY = eng.y + eng.height / 2;

    // 3) measure the translation at the fixed font size
    const item = document.createElementNS(SVG_NS, "g");
    g.appendChild(item);
    const t = document.createElementNS(SVG_NS, "text");
    t.setAttribute("text-anchor", "middle");
    t.setAttribute("fill", "#222222");
    t.style.fontFamily = C.fontFamily;
    t.style.fontWeight = "700";
    t.style.fontSize = `${FS}px`;
    t.style.userSelect = "none";

    const measureNode = document.createElementNS(SVG_NS, "text");
    measureNode.style.fontFamily = C.fontFamily;
    measureNode.style.fontWeight = "700";
    measureNode.style.fontSize = `${FS}px`;
    measureNode.setAttribute("visibility", "hidden");
    item.appendChild(measureNode);
    const measure = (s) => {
      measureNode.textContent = s;
      try {
        return measureNode.getComputedTextLength();
      } catch {
        return s.length * FS * 0.6;
      }
    };

    // wrap only if one line is wider than MAX_LINE_W (font stays 20px)
    const words = tr.split(/\s+/).filter(Boolean);
    const wrapAt = (maxW) => {
      const out = [];
      let cur = "";
      words.forEach((w) => {
        const test = cur ? `${cur} ${w}` : w;
        if (cur && measure(test) > maxW) {
          out.push(cur);
          cur = w;
        } else {
          cur = test;
        }
      });
      if (cur) out.push(cur);
      return out;
    };
    let lines = wrapAt(MAX_LINE_W);
    for (let w = MAX_LINE_W; lines.length > MAX_LINES && w < vb.width; w += FS * 2) lines = wrapAt(w);
    const textW = Math.max(...lines.map(measure), 1);
    item.removeChild(measureNode);

    // 4) compact bubble around the text
    const bw = textW + PAD_X * 2;
    const bh = lines.length * LINE_H + PAD_Y * 2;
    let bx = clamp(targetX - bw / 2, vb.x, vb.x + vb.width - bw);

    // try above and below; keep the one that overlaps the least (earlier bubbles, other English text)
    const obstacles = [
      ...placed,
      ...engInfo.filter((_, k) => k !== i).map((e) => e.eng),
    ];
    const candidates = [
      { above: true, by: eng.y - GAP - bh },
      { above: false, by: eng.y + eng.height + GAP },
    ].map((c) => {
      const rect = { x: bx, y: c.by, width: bw, height: bh };
      let score = obstacles.reduce((sum, o) => sum + overlapArea(rect, o), 0);
      if (c.by < vb.y || c.by + bh > vb.y + vb.height) score += 1e9; // would leave the SVG
      return { ...c, score };
    });
    const pick = candidates.reduce((a, b) => (b.score < a.score ? b : a));
    const above = pick.above;
    const by = pick.by;
    placed.push({ x: bx, y: by, width: bw, height: bh });

    // tail base on the bubble edge facing the English; tip exactly at the target point
    const edgeY = above ? by + bh : by;
    const baseX = clamp(targetX, bx + RADIUS + TAIL_HALF, bx + bw - RADIUS - TAIL_HALF);
    const bL = baseX - TAIL_HALF;
    const bR = baseX + TAIL_HALF;

    const x1 = bx + bw;
    const y1 = by + bh;
    const top = above
      ? `M ${bx + RADIUS} ${by} H ${x1 - RADIUS}`
      : `M ${bx + RADIUS} ${by} H ${bL} L ${targetX} ${targetY} L ${bR} ${by} H ${x1 - RADIUS}`;
    const bottom = above
      ? `H ${bR} L ${targetX} ${targetY} L ${bL} ${y1} H ${bx + RADIUS}`
      : `H ${bx + RADIUS}`;
    const d =
      `${top} Q ${x1} ${by} ${x1} ${by + RADIUS} V ${y1 - RADIUS} Q ${x1} ${y1} ${x1 - RADIUS} ${y1} ` +
      `${bottom} Q ${bx} ${y1} ${bx} ${y1 - RADIUS} V ${by + RADIUS} Q ${bx} ${by} ${bx + RADIUS} ${by} Z`;

    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    path.setAttribute("fill", C.transBg || "#ffffff");
    path.setAttribute("stroke", "#444444");
    path.setAttribute("stroke-width", "1.5");
    path.setAttribute("stroke-linejoin", "round");
    item.appendChild(path);

    // 5) translation centred in the bubble
    item.appendChild(t);
    const cxText = bx + bw / 2;
    const topY = by + (bh - lines.length * LINE_H) / 2;
    lines.forEach((line, li) => {
      const ts = document.createElementNS(SVG_NS, "tspan");
      ts.setAttribute("x", String(cxText));
      ts.setAttribute("y", String(topY + LINE_H * (li + 0.5)));
      ts.setAttribute("dy", "0.35em");
      ts.textContent = line;
      t.appendChild(ts);
    });

    if (C.transDebug) {
      const dbg = document.createElementNS(SVG_NS, "rect");
      dbg.setAttribute("x", String(eng.x));
      dbg.setAttribute("y", String(eng.y));
      dbg.setAttribute("width", String(eng.width));
      dbg.setAttribute("height", String(eng.height));
      dbg.setAttribute("fill", "none");
      dbg.setAttribute("stroke", "red");
      dbg.setAttribute("stroke-width", "1");
      dbg.setAttribute("stroke-dasharray", "4 3");
      g.appendChild(dbg);
    }

    logBlock("🌐 TRANSLATION CALLOUT", [
      `Original: ${r.text}`,
      `Translation: ${tr}`,
      `English bbox (${source}): x=${f1(eng.x)}, y=${f1(eng.y)}, width=${f1(eng.width)}, height=${f1(eng.height)}`,
      `Target center: x=${f1(targetX)}, y=${f1(targetY)}`,
      `Bubble (${above ? "above" : "below"}): x=${f1(bx)}, y=${f1(by)}, width=${f1(bw)}, height=${f1(bh)}`,
      `Pointer tip: x=${f1(targetX)}, y=${f1(targetY)}`,
      `Font size: ${FS}px | lines: ${lines.length}`,
    ]);
  });
}

// ============================================================================
// ID helpers + audio playback
// ============================================================================
function extractEncodedIdOnly(fullId) {
  const match = fullId.match(/(_x28_.*?_x29_)/);
  return match ? match[1] : null;
}

function extractOriginalPath(encoded) {
  if (!encoded) return null;
  const decoded = encoded.replace(/_x([0-9A-Fa-f]{2})_/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  const match = decoded.match(/\((.*?)\)/);
  if (!match) return null;
  return match[1].replace(/_/g, " ").replace(/\s{2,}/g, " ").trim();
}

function decodeIdToOriginalPath(input) {
  if (!input) return "";
  const decoded = input.replace(/_x([0-9A-Fa-f]{2})_/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  const match = decoded.match(/\((.*?)\)/);
  return match ? match[1] : decoded;
}

let currentAudio;

function stopCurrentAudio() {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
  }
}

function playAudio(path) {
  stopCurrentAudio();
  const audio = new Audio(path);
  currentAudio = audio;
  audio.play().catch((err) => logAudioError("hover", path, err, audio.error));
}

function playGuidedAudio(path, onEnded, onError) {
  stopCurrentAudio();
  const audio = new Audio(path);
  currentAudio = audio;
  audio.onended = onEnded;
  audio.play().catch((err) => onError(err, audio.error));
}

// ============================================================================
// HAND MOVE SOUND ("swishhh!")
// ----------------------------------------------------------------------------
// A short, soft whoosh played ONCE each time the guided hand starts moving to
// the next reading layer. It is deliberately kept OUT of `currentAudio`, so it
// never pauses/replaces the narration (stopCurrentAudio() does not touch it).
//
// Two ways to supply the sound (change them HERE, nowhere else):
//   1) Default  → `url: null`  : a built-in synthesized swish (Web Audio API),
//                                 no asset file needed.
//   2) Your own → set `url`    : e.g. "https://file.fefdygo.com/sfx/hand-swish.mp3"
//                                 (any short 200–500 ms file; played at `volume`).
//
// To change the loudness:  edit `volume` (0 – 1).  Keep it low (≈0.2–0.35).
// To change the length:    edit `durationMs` (synth only; 200–500 recommended).
// To turn it off:          set `enabled: false`.
// ============================================================================
const HAND_MOVE_SFX = {
  enabled: true,
  url: null,        // ← put a sound-file URL here to use your own swish instead of the synth
  volume: 0.3,      // ← overall loudness (0 – 1). Subtle on purpose.
  durationMs: 360,  // ← synth swish length (ms). File mode uses the file's own length.
  minGapMs: 90,     // ← ignore a new trigger closer than this to the previous one (debounce)
};

let handSfxCtx = null;      // shared AudioContext (created lazily)
let handSfxNoise = null;    // cached white-noise buffer
let handSfxActive = null;   // the swish currently sounding: { master, nodes }
let handSfxLastAt = 0;      // timestamp of the last accepted trigger
let handSfxFileAudio = null; // reusable <audio> element for file mode

function getHandSfxContext() {
  if (handSfxCtx) return handSfxCtx;
  const Ctor = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!Ctor) return null;
  try {
    handSfxCtx = new Ctor();
  } catch {
    handSfxCtx = null;
  }
  return handSfxCtx;
}

// Browsers only allow audio after a user gesture (same rule as the narration).
// The first click / tap / key press creates + resumes the context so later
// hand moves can play. Returns a cleanup function for useEffect.
function armHandMoveSoundUnlock() {
  if (typeof window === "undefined") return undefined;
  const events = ["pointerdown", "touchstart", "keydown"];
  const unlock = () => {
    const ctx = getHandSfxContext();
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    events.forEach((e) => window.removeEventListener(e, unlock, true));
  };
  events.forEach((e) => window.addEventListener(e, unlock, true));
  return () => events.forEach((e) => window.removeEventListener(e, unlock, true));
}

// Fade out and stop the swish that is still sounding (prevents messy overlap).
function stopActiveHandSwish(ctx) {
  const active = handSfxActive;
  if (!active) return;
  handSfxActive = null;
  try {
    const now = ctx.currentTime;
    active.master.gain.cancelScheduledValues(now);
    active.master.gain.setValueAtTime(active.master.gain.value, now);
    active.master.gain.linearRampToValueAtTime(0, now + 0.03);
    active.nodes.forEach((n) => {
      try {
        n.stop(now + 0.04);
      } catch {
        /* already stopped */
      }
    });
  } catch {
    /* ignore */
  }
}

function startSynthSwish(ctx) {
  const now = ctx.currentTime;
  const dur = HAND_MOVE_SFX.durationMs / 1000;
  const vol = HAND_MOVE_SFX.volume;

  stopActiveHandSwish(ctx); // restart cleanly if a previous swish is still fading

  if (!handSfxNoise) {
    const len = Math.floor(ctx.sampleRate * 0.6);
    handSfxNoise = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = handSfxNoise.getChannelData(0);
    for (let i = 0; i < len; i += 1) data[i] = Math.random() * 2 - 1;
  }

  const master = ctx.createGain();
  master.gain.value = 1;
  master.connect(ctx.destination);

  // airy "whoosh": noise through a band-pass whose centre sweeps up then settles
  const noise = ctx.createBufferSource();
  noise.buffer = handSfxNoise;
  const band = ctx.createBiquadFilter();
  band.type = "bandpass";
  band.Q.value = 0.9;
  band.frequency.setValueAtTime(450, now);
  band.frequency.exponentialRampToValueAtTime(2600, now + dur * 0.55);
  band.frequency.exponentialRampToValueAtTime(1200, now + dur);
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.0001, now);
  noiseGain.gain.exponentialRampToValueAtTime(vol, now + dur * 0.3); // soft swell in
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + dur);    // gentle tail out
  noise.connect(band);
  band.connect(noiseGain);
  noiseGain.connect(master);

  // tiny rising sine "splish" under the whoosh for a cheerful cartoon feel
  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(520, now);
  osc.frequency.exponentialRampToValueAtTime(1040, now + dur * 0.5);
  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(0.0001, now);
  oscGain.gain.exponentialRampToValueAtTime(vol * 0.25, now + dur * 0.25);
  oscGain.gain.exponentialRampToValueAtTime(0.0001, now + dur * 0.9);
  osc.connect(oscGain);
  oscGain.connect(master);

  const stopAt = now + dur + 0.05;
  noise.start(now);
  osc.start(now);
  noise.stop(stopAt);
  osc.stop(stopAt);

  const entry = { master, nodes: [noise, osc] };
  handSfxActive = entry;
  noise.onended = () => {
    if (handSfxActive === entry) handSfxActive = null;
    try {
      master.disconnect();
    } catch {
      /* ignore */
    }
  };
}

function playHandMoveSound() {
  if (!HAND_MOVE_SFX.enabled) return;

  // debounce: very quick back-to-back moves don't stack into a messy sound
  const t = Date.now();
  if (t - handSfxLastAt < HAND_MOVE_SFX.minGapMs) return;

  // respect autoplay rules: stay silent until the user has interacted with the page
  if (typeof navigator !== "undefined" && navigator.userActivation && !navigator.userActivation.hasBeenActive) return;

  handSfxLastAt = t;

  // file mode (own sound file): restart the same element, never overlap copies
  if (HAND_MOVE_SFX.url) {
    if (!handSfxFileAudio) {
      handSfxFileAudio = new Audio(HAND_MOVE_SFX.url);
      handSfxFileAudio.preload = "auto";
    }
    const a = handSfxFileAudio;
    a.volume = Math.max(0, Math.min(1, HAND_MOVE_SFX.volume));
    a.currentTime = 0;
    a.play().catch((err) => logAudioError("hand move sfx", HAND_MOVE_SFX.url, err, a.error));
    return;
  }

  // synth mode (default)
  const ctx = getHandSfxContext();
  if (!ctx) return;
  if (ctx.state === "running") {
    startSynthSwish(ctx);
  } else {
    ctx
      .resume()
      .then(() => {
        // only play if it is still "now" — never a late swish after the hand has arrived
        if (ctx.state === "running" && Date.now() - t < 150) startSynthSwish(ctx);
      })
      .catch(() => {});
  }
}

function getCurrentGoogleTranslateLanguage() {
  const select = document.querySelector(".goog-te-combo");
  return select ? select.value : null;
}

const SHADOW_OFFSETS = [[0, 0], [1, 1], [-1, -1], [1, -1], [-1, 1], [1, 0], [-1, 0], [0, 1], [0, -1]];
const outline = (color) => SHADOW_OFFSETS.map(([x, y]) => `drop-shadow(${x}px ${y}px 0 ${color})`).join(" ");
const ALL_COLOR = "rgb(220, 80, 80)";

// ============================================================================
// Component
// ============================================================================
const LayeredSVG = ({ src, category, subtopic_data }) => {
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const helperRef = useRef(null);
  const [svgContent, setSvgContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [, startTransition] = useTransition();
  const adioURL = "https://file.fefdygo.com/";
  const [firstHighlight, setFirstHighlight] = useState(false);
  const [, setReturnAudioIds] = useState([]);
  const [, setAudioIds] = useState([]);
  const audioIdsRef = useRef([]);
  const compIds = useRef([]);
  const returnAudioIdsRef = useRef([]);
  const { auth, breadcrumbs, selectedLang } = useAuth();
  const [checkFirstHit, setCheckFirstHit] = useState(false);

  // Guided reading (refs mirror state because listeners are created once per SVG parse)
  const [currentReadingIndex, setCurrentReadingIndex] = useState(0);
  const [isGuidedReading, setIsGuidedReading] = useState(true);
  const [, setGuidedItemPlayed] = useState(false);
  const currentReadingIndexRef = useRef(0);
  const isGuidedReadingRef = useRef(true);
  const guidedItemPlayedRef = useRef(false);
  const [isGuidedModeEnabled, setIsGuidedModeEnabled] = useState(true);

  const readingOrderRef = useRef([]);
  const [handPosition, setHandPosition] = useState(null);
  const [startPosition, setStartPosition] = useState(null);
  const updateHandPositionRef = useRef(() => { });

  // Remembers the reading index the hand was last placed at, so the movement
  // sound only fires when the index really CHANGES (null = hand not placed yet).
  const lastMoveSoundIndexRef = useRef(null);

  // OCR / translation state
  const [ocrData, setOcrData] = useState(null);
  const [ocrStatus, setOcrStatus] = useState("idle");
  const [ocrSvgVersion, setOcrSvgVersion] = useState(0);
  const ocrLayerCacheRef = useRef(ocrLayerStore);
  const [googleLang, setGoogleLang] = useState(() => getCurrentGoogleTranslateLanguage() || "en");

  const ctxLang = typeof selectedLang === "string" ? selectedLang : selectedLang?.code;
  const langCode = String(ctxLang || googleLang || "en").toLowerCase();

  const handleGuidedModeToggle = () => {
    setIsGuidedModeEnabled((prev) => {
      const next = !prev;
      if (next) {
        currentReadingIndexRef.current = 0;
        setCurrentReadingIndex(0);
        guidedItemPlayedRef.current = false;
        setGuidedItemPlayed(false);
        isGuidedReadingRef.current = true;
        setIsGuidedReading(true);
      } else {
        stopCurrentAudio();
        isGuidedReadingRef.current = false;
        setIsGuidedReading(false);
      }
      return next;
    });
  };

  // Unlock the hand-move sound on the first user gesture (autoplay policy)
  useEffect(() => armHandMoveSoundUnlock(), []);

  // Fetch the SVG once per src
  const MIN_LOADER_TIME = 500;
  useEffect(() => {
    const controller = new AbortController();
    const startTime = Date.now();
    setIsLoading(true);
    setError("");

    fetch(src, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) console.error(`❌ SVG request failed: ${src} (HTTP ${res.status})`);
        return res.text();
      })
      .then((data) => {
        const cleanedXml = data.replace(/<i:[^>]+>[\s\S]*?<\/i:[^>]+>/g, "");
        const remaining = Math.max(MIN_LOADER_TIME - (Date.now() - startTime), 0);
        setTimeout(() => {
          startTransition(() => {
            setSvgContent(cleanedXml);
            setIsLoading(false);
          });
        }, remaining);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error(`❌ SVG load error: ${src}`, err);
        const remaining = Math.max(MIN_LOADER_TIME - (Date.now() - startTime), 0);
        setTimeout(() => {
          setError("Sorry, the file could not be loaded.");
          setIsLoading(false);
        }, remaining);
      });

    return () => controller.abort();
  }, [src]);

  // Parse and inject SVG, add events
  useEffect(() => {
    if (!svgContent || !containerRef.current) return;

    requestAnimationFrame(() => {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
      const svgElement = svgDoc.querySelector("svg");
      const parserError = svgDoc.querySelector("parsererror");

      if (!svgElement) {
        console.error("❌ SVG parse error: no <svg> root element", parserError?.textContent?.trim().slice(0, 300));
        setError("Sorry, the file could not be loaded.");
        return;
      }
      if (parserError) {
        console.warn("⚠️ SVG parse warning:", parserError.textContent.trim().slice(0, 300));
      }

      const svgContainer = containerRef.current;
      svgContainer.innerHTML = "";
      svgContainer.appendChild(svgElement);

      const highlightColor = "#689E41";
      const textHighlight = "#e80707";

      const resetHighlights = () => {
        svgElement.querySelectorAll(".highlight").forEach((el) => {
          el.classList.remove("highlight", "boom-pulse");
          el.style.filter = "";
        });
        svgElement.querySelectorAll("line").forEach((line) => {
          line.classList.remove("boom-pulse");
          line.style.filter = "";
          line.style.stroke = "";
        });
        svgElement.querySelectorAll("text").forEach((text) => {
          text.classList.remove("boom-pulse-text");
          text.style.fill = "";
        });
      };

      const updateNCheckCompAudioIds = async (audioIds, compAudioIds, type) => {
        if (!audioIds || audioIds.length === 0) return;
        if (type === "check" && checkFirstHit) return;
        if (type === "check") setCheckFirstHit(true);

        try {
          const subtopic = breadcrumbs.find((item) => item.path === "/categories")?.id || null;
          const data = await readHistoryEntryService(
            auth.token,
            audioIds,
            compAudioIds,
            type,
            subtopic,
            category,
            subtopic_data
          );
          if (data.status === 200 || data.status === 400) {
            setReturnAudioIds(data.ret_audio_ids);
            returnAudioIdsRef.current = data.ret_audio_ids;
          }
        } catch (err) {
          console.error(`read-history API call failed (${type})`, err);
        }
      };

      // suppressAudio = true skips only the final playAudio() of the manual-group branch.
      const handleEnter = async (allIds, id, isText, isAll, suppressAudio = false) => {
        const encodedId = extractEncodedIdOnly(id);
        const path = extractOriginalPath(id);
        if (!path) return;
        const cleanId = decodeIdToOriginalPath(path);

        if (!isAll) resetHighlights();

        if (isText) {
          const color = isAll ? ALL_COLOR : highlightColor;
          const target =
            svgElement.querySelector(`path#${CSS.escape(cleanId)}`) ||
            svgElement.querySelector(`g#${CSS.escape(cleanId)}`);
          if (target) {
            target.classList.add("highlight");
            target.classList.remove("boom-pulse");
            target.style.filter = outline(color);
          }
          const line = svgElement.querySelector(`line[id^="${CSS.escape(encodedId)}"]`);
          if (line) {
            line.classList.remove("boom-pulse");
            line.style.filter = outline(color);
            line.style.stroke = color;
          }
          if (!isAll && !compIds.current.includes(cleanId)) {
            compIds.current.push(decodeIdToOriginalPath(id));
          }
          if (!isAll) playAudio(adioURL + cleanId);
        } else if (isAll) {
          audioIdsRef.current = allIds;
          await updateNCheckCompAudioIds(audioIdsRef.current, [], "check");
          const normalize = (str) => str.trim().replace(/^\/+|\/+$/g, "");
          const cleanIdPath = decodeIdToOriginalPath(id);

          if (!returnAudioIdsRef.current.some((rid) => normalize(rid) === normalize(cleanIdPath))) {
            return false;
          }

          const group = svgElement.querySelector(`g#${CSS.escape(id)}`);
          if (!group) return;
          group.classList.add("highlight", "boom-pulse");
          const shadowFilter = SHADOW_OFFSETS.slice(0, 5)
            .map(([x, y]) => `drop-shadow(${x}px ${y}px 0 rgb(255, 80, 80))`)
            .join(" ");
          group.style.filter = shadowFilter;

          const line = svgElement.querySelector(`line#${CSS.escape(id)}`);
          if (line) {
            line.classList.add("boom-pulse");
            line.style.filter = shadowFilter;
            line.style.stroke = "#f3ffc4";
          }
          const text = svgElement.querySelector(`text#${CSS.escape(id)}`);
          if (text) {
            text.classList.add("boom-pulse-text");
            text.style.fill = ALL_COLOR;
          }
        } else {
          const group = svgElement.querySelector(`g#${CSS.escape(id)}`);
          if (!group) return;
          group.classList.add("highlight");
          group.classList.remove("boom-pulse", "boom-pulse-text");
          group.style.filter = outline(highlightColor);

          const line = svgElement.querySelector(`line#${CSS.escape(id)}`);
          if (line) {
            line.classList.remove("boom-pulse");
            line.style.filter = outline(highlightColor);
            line.style.stroke = "#f3ffc4";
          }
          const text = svgElement.querySelector(`text#${CSS.escape(id)}`);
          if (text) {
            text.classList.remove("boom-pulse-text");
            text.style.fill = textHighlight;
          }

          if (!compIds.current.includes(cleanId)) {
            compIds.current.push(decodeIdToOriginalPath(id));
          }
          // One audio path for every language and mode (same path Guided Mode plays)
          if (!suppressAudio) playAudio(adioURL + cleanId);
        }

        if (compIds.current.length > 0 && !isAll) {
          updateNCheckCompAudioIds(audioIdsRef.current, compIds.current, "update");
        }
      };

      const handleLeave = () => {
        resetHighlights();
      };

      // Support both export styles: bare <image id="_x28_..."> gets wrapped in a <g> that takes the id.
      svgElement.querySelectorAll("image[id*='_x28_']").forEach((img) => {
        if (img.closest("g[id*='_x28_']")) return;
        const wrapper = document.createElementNS(SVG_NS, "g");
        wrapper.setAttribute("id", img.id);
        img.parentNode.insertBefore(wrapper, img);
        wrapper.appendChild(img);
        img.removeAttribute("id");
      });

      // Reading groups: <g> with an encoded audio path, never hand markers.
      const allGroups = Array.from(svgElement.querySelectorAll("g[id]"));
      const groups = allGroups.filter(
        (group) => !group.id.startsWith("hand_x5F_") && group.id.includes("_x28_") && group.id.includes("_x29_")
      );

      // A candidate nested inside another candidate is not a separate layer (outermost wins).
      const candidateSet = new Set(groups);
      const nestedReadingAncestor = new Map();
      groups.forEach((g) => {
        let p = g.parentElement;
        while (p) {
          if (candidateSet.has(p)) nestedReadingAncestor.set(g, p);
          p = p.parentElement;
        }
      });
      const readingGroups = groups.filter((g) => !nestedReadingAncestor.has(g));

      // Hand markers: positional anchors, hidden but still measurable.
      const handMarkers = Array.from(svgElement.querySelectorAll('[id^="hand_x5F_"]'));
      handMarkers.forEach((marker) => {
        marker.style.opacity = "0";
        marker.style.pointerEvents = "none";
      });

      // Reading item N ↔ hand marker N, by natural SVG order.
      const readingOrder = readingGroups.map((group, idx) => ({
        index: idx + 1,
        groupId: group.id,
        audioPath: decodeIdToOriginalPath(extractOriginalPath(group.id)),
        markerEl: handMarkers[idx] || null,
      }));
      readingOrderRef.current = readingOrder;

      logSvgSummary(readingOrder, handMarkers, groups.length - readingGroups.length);

      requestAnimationFrame(() => updateHandPositionRef.current());

      const readingOrderIndexByGroupId = {};
      readingOrder.forEach((item, i) => {
        readingOrderIndexByGroupId[item.groupId] = i;
      });

      const playGuidedReadingItem = (idx) => {
        const item = readingOrder[idx];
        if (!item) return;

        guidedItemPlayedRef.current = true;
        setGuidedItemPlayed(true);

        const fullUrl = adioURL + item.audioPath;
        if (!item.audioPath) console.error(`❌ Layer ${item.index} has no audio path (${item.groupId})`);

        playGuidedAudio(
          fullUrl,
          () => {
            const nextIndex = idx + 1;
            guidedItemPlayedRef.current = false;
            setGuidedItemPlayed(false);
            const next = nextIndex >= readingOrder.length ? 0 : nextIndex; // loop back to the first item
            currentReadingIndexRef.current = next;
            setCurrentReadingIndex(next);
          },
          (err, mediaError) => logAudioError(`guided layer ${item.index}`, fullUrl, err, mediaError)
        );
      };

      const ids = [];
      groups.forEach((group) => {
        const decoded = decodeIdToOriginalPath(group.id);
        if (!ids.includes(decoded)) ids.push(decoded);

        group.style.cursor = "pointer";
        group.addEventListener("mouseenter", () => {
          if (isGuidedReadingRef.current) {
            if (nestedReadingAncestor.has(group)) return;
            const idx = readingOrderIndexByGroupId[group.id];
            if (idx === undefined || idx !== currentReadingIndexRef.current) return;
            if (!guidedItemPlayedRef.current) playGuidedReadingItem(idx);
            handleEnter([], group.id, false, false, true);
            return;
          }
          handleEnter([], group.id, false, false, false);
        });
        group.addEventListener("mouseleave", handleLeave);
      });

      const texts = svgElement.querySelectorAll("text[id]");
      texts.forEach((text) => {
        const decoded = decodeIdToOriginalPath(text.id);
        if (!ids.includes(decoded)) ids.push(decoded);

        text.style.cursor = "pointer";
        text.addEventListener("mouseenter", () => handleEnter([], text.id, true, false));
        text.addEventListener("mouseleave", handleLeave);
      });
      setAudioIds(ids);

      if (!firstHighlight) {
        groups.forEach((group) => {
          group.style.cursor = "pointer";
          handleEnter(ids, group.id, false, true);
        });
        texts.forEach((text) => handleEnter(ids, text.id, true, true));
        setFirstHighlight(true);
      }

      if (OCR_CONFIG.enabled) setOcrSvgVersion((v) => v + 1);
    });
  }, [svgContent]);

  // Hand-pointer position (recomputed on index change, resize, fullscreen, container resize)
  useEffect(() => {
    const updateHandPosition = () => {
      if (!isGuidedReadingRef.current) return;
      const order = readingOrderRef.current;
      const item = order[currentReadingIndexRef.current];
      const wrapper = wrapperRef.current;
      if (!item || !containerRef.current || !wrapper) return;

      const markerEl = item.markerEl;
      if (!markerEl) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const markerRect = markerEl.getBoundingClientRect();

      const helperEl = helperRef.current;
      const helperWidth = helperEl ? helperEl.offsetWidth : 70;
      const helperHeight = helperEl ? helperEl.offsetHeight : 60;

      const markerCenterX = markerRect.left - wrapperRect.left + markerRect.width / 2;
      const markerCenterY = markerRect.top - wrapperRect.top + markerRect.height / 2;

      const top = Math.max(4, Math.min(markerCenterY - helperHeight / 2, wrapperRect.height - helperHeight - 4));
      const left = Math.max(4, Math.min(markerCenterX - helperWidth / 2, wrapperRect.width - helperWidth - 4));
      setHandPosition({ top, left });

      // "Start" label always sits under the first reading element
      const firstItem = order[0];
      if (firstItem?.markerEl) {
        const fr = firstItem.markerEl.getBoundingClientRect();
        const startWidth = 70;
        const startHeight = 30;
        const firstCenterX = fr.left - wrapperRect.left + fr.width / 2;
        const firstCenterY = fr.top - wrapperRect.top + fr.height / 2;
        const startTop = firstCenterY + fr.height / 2 + 12;
        const startLeft = firstCenterX - startWidth / 2;
        setStartPosition({
          top: Math.max(4, Math.min(startTop, wrapperRect.height - startHeight - 4)),
          left: Math.max(4, Math.min(startLeft, wrapperRect.width - startWidth - 4)),
        });
      }
    };

    updateHandPositionRef.current = updateHandPosition;

    // ┌──────────────────────────────────────────────────────────────────────┐
    // │ 🔊 HAND MOVE SOUND TRIGGER  (the ONLY place the swish is played)     │
    // │                                                                      │
    // │ This effect re-runs whenever `currentReadingIndex` changes, i.e. at  │
    // │ the exact moment the hand is about to glide to the next layer (the   │
    // │ rAF below then sets the new top/left and the CSS transition starts). │
    // │                                                                      │
    // │ It fires ONCE per real index change. It does NOT fire for:           │
    // │   • the first placement of the hand (previous index is null)         │
    // │   • window resize / fullscreen / ResizeObserver re-measuring         │
    // │     (those call updateHandPosition directly, not this block)         │
    // │   • Guided Mode being off, or a layer without a hand marker          │
    // │                                                                      │
    // │ Change the sound file / volume in HAND_MOVE_SFX (near the audio      │
    // │ helpers, above the component).                                       │
    // └──────────────────────────────────────────────────────────────────────┘
    const previousIndex = lastMoveSoundIndexRef.current;
    lastMoveSoundIndexRef.current = currentReadingIndex;
    if (
      previousIndex !== null &&
      previousIndex !== currentReadingIndex &&
      isGuidedReadingRef.current &&
      readingOrderRef.current[currentReadingIndex]?.markerEl
    ) {
      playHandMoveSound(); // 🔊 "swishhh!" — hand starts moving to the next position
    }

    // Double rAF: the first pass uses a fallback helper size, the second refines it once rendered.
    requestAnimationFrame(() => {
      updateHandPosition();
      requestAnimationFrame(updateHandPosition);
    });

    window.addEventListener("resize", updateHandPosition);
    document.addEventListener("fullscreenchange", updateHandPosition);
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      resizeObserver = new ResizeObserver(updateHandPosition);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateHandPosition);
      document.removeEventListener("fullscreenchange", updateHandPosition);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [currentReadingIndex, isGuidedReading]);

  // Track the Google Translate <select> (fallback language source)
  useEffect(() => {
    const onChange = (e) => {
      const t = e.target;
      if (t && t.classList && t.classList.contains("goog-te-combo")) setGoogleLang(t.value || "en");
    };
    document.addEventListener("change", onChange, true);
    return () => document.removeEventListener("change", onChange, true);
  }, []);

  // OCR every reading layer once per SVG (cached per layer)
  useEffect(() => {
    if (!OCR_CONFIG.enabled || !ocrSvgVersion) return undefined;
    const svgEl = containerRef.current && containerRef.current.querySelector("svg");
    if (!svgEl) return undefined;

    const needed = OCR_CONFIG.retrievalOnly || langCode !== "en" || OCR_CONFIG.prefetchInEnglish;
    if (!needed) return undefined;

    const cached = ocrCache.get(src);
    if (cached && cached.status === "done") {
      setOcrData(cached.data);
      setOcrStatus("done");
      return undefined;
    }
    if (cached && cached.status === "failed") {
      setOcrStatus("error");
      return undefined;
    }

    let cancelled = false;
    setOcrStatus("running");
    runLayerOcr(svgEl, src, readingOrderRef.current, ocrLayerCacheRef.current)
      .then((data) => {
        if (cancelled) return;
        setOcrData(data);
        setOcrStatus("done");
      })
      .catch((err) => {
        if (cancelled) return;
        setOcrStatus("error");
        console.error(`❌ OCR failed for ${src}: ${err?.message || err}`);
      });
    return () => {
      cancelled = true;
    };
  }, [ocrSvgVersion, langCode, src]);

  // Translate cached layer text and draw the overlay (disabled while retrievalOnly)
  useEffect(() => {
    const svgEl = containerRef.current && containerRef.current.querySelector("svg");
    if (!svgEl) return undefined;

    const removeOverlay = () => svgEl.querySelectorAll(".lsvg-ocr-overlay").forEach((n) => n.remove());

    if (OCR_CONFIG.retrievalOnly) {
      removeOverlay();
      return undefined;
    }

    const textLayers = ocrData && ocrData.key === src ? ocrData.layers.filter((l) => l.text) : [];
    if (langCode === "en" || !textLayers.length) {
      removeOverlay();
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const translations = await Promise.all(
          textLayers.map((l) =>
            translateText(l.text, langCode)
              .then((out) => {
                l.translations[langCode] = out;
                return out;
              })
              .catch((err) => {
                console.error(`❌ Translation failed (layer ${l.layer}, "${l.text}" → ${langCode}): ${err?.message || err}`);
                return null;
              })
          )
        );
        if (cancelled) return;

        console.log(`🌐 TRANSLATION → ${langCode}`);
        console.table(
          textLayers.map((l, i) => ({
            Layer: l.layer,
            "Marker value": l.debug?.rawText,
            "English (sent)": l.text,
            Language: langCode,
            Translation: translations[i] || "FAILED",
          }))
        );

        removeOverlay();
        buildTranslationOverlay(svgEl, textLayers, translations, ocrData.vb);
      } catch (err) {
        console.error("❌ Overlay error", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ocrData, langCode, ocrSvgVersion, src]);

  const handleFullscreen = () => {
    const elem = containerRef.current;
    if (!document.fullscreenElement) {
      elem.requestFullscreen?.();
      elem.webkitRequestFullscreen?.();
      elem.msRequestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  return (
    <div
      className="layered-svg-wrapper"
      ref={wrapperRef}
      style={{ position: "relative" }}
      data-ocr-status={ocrStatus}
    >
      <style>{`
        .lsvg-guide-toggle {
          position: absolute;
          top: 10px;
          left: 10px;
          z-index: 20;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 5px 6px 5px 6px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.94);
          border: 1px solid rgba(0, 0, 0, 0.06);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
          font-size: 12px;
          font-weight: 600;
          line-height: 1;
          color: #7a828a;
          user-select: none;
          transition: background 0.25s ease, border-color 0.25s ease,
                      box-shadow 0.25s ease, color 0.25s ease;
        }
        .lsvg-guide-toggle.is-on {
          background: #f6faf0;
          border-color: rgba(104, 158, 65, 0.35);
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08),
                      0 0 0 3px rgba(104, 158, 65, 0.12);
          color: #2f4f1b;
        }
        .lsvg-guide-icon {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #eef0f2;
          color: #a0a7ae;
          flex-shrink: 0;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .lsvg-guide-toggle.is-on .lsvg-guide-icon {
          background: #e3efd6;
          color: #689e41;
        }
        .lsvg-guide-icon svg { width: 13px; height: 13px; }
        .lsvg-guide-label { white-space: nowrap; }
        .lsvg-guide-status {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #a0a7ae;
          min-width: 20px;
          text-align: right;
        }
        .lsvg-guide-toggle.is-on .lsvg-guide-status { color: #689e41; }

        .lsvg-switch {
          position: relative;
          width: 36px;
          height: 20px;
          padding: 0;
          border: none;
          border-radius: 999px;
          background: #cfd4d9;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.25s ease;
        }
        .lsvg-switch.is-on { background: #689e41; }
        .lsvg-switch:focus-visible {
          outline: 2px solid #689e41;
          outline-offset: 2px;
        }
        .lsvg-switch-knob {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
          transition: transform 0.25s ease;
        }
        .lsvg-switch.is-on .lsvg-switch-knob { transform: translateX(16px); }

        @keyframes lsvg-hand-float {
          0%, 100% { transform: translateY(0) rotate(-10deg) scale(1); }
          25% { transform: translateY(-10px) rotate(-4deg) scale(1.04); }
          50% { transform: translateY(0) rotate(2deg) scale(1); }
          75% { transform: translateY(-7px) rotate(-5deg) scale(1.03); }
        }

        @keyframes lsvg-hand-tap {
          0%, 100% { transform: scale(1); }
          45% { transform: scale(0.88); }
          65% { transform: scale(1.08); }
        }

        .lsvg-hand-pointer {
          line-height: 0;
          animation:
            lsvg-hand-float 1.8s ease-in-out infinite,
            lsvg-hand-tap 1.8s ease-in-out infinite;
          filter:
            drop-shadow(0 5px 4px rgba(0, 0, 0, 0.20))
            drop-shadow(0 0 3px rgba(255, 255, 255, 0.7));
          transform-origin: center bottom;
        }

        .lsvg-hand-pointer svg {
          width: 82px;
          height: 82px;
          display: block;
          overflow: visible;
        }

        .lsvg-start-label {
          position: relative;
          margin-top: -4px;
          padding: 3px 10px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.02em;
          line-height: 1.2;
          color: #4e7d2c;
          background: #f6faf0;
          border: 1px solid rgba(104, 158, 65, 0.3);
          border-radius: 999px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
          white-space: nowrap;
          cursor: default;
          animation: lsvg-label-breathe 2.6s ease-in-out infinite;
        }
        .lsvg-start-label::before {
          content: "";
          position: absolute;
          top: -4px;
          left: 50%;
          width: 6px;
          height: 6px;
          background: #f6faf0;
          border-left: 1px solid rgba(104, 158, 65, 0.3);
          border-top: 1px solid rgba(104, 158, 65, 0.3);
          transform: translateX(-50%) rotate(45deg);
        }

        @media (max-width: 768px) {
          .lsvg-guide-toggle { top: 8px; right: 8px; gap: 6px; padding: 4px 5px; font-size: 11px; }
          .lsvg-guide-icon { width: 20px; height: 20px; }
          .lsvg-hand-pointer svg { width: 70px; height: 70px; }
          .lsvg-start-label { font-size: 11px; padding: 2px 8px; }
        }

        @media (max-width: 480px) {
          .lsvg-guide-status { display: none; }
          .lsvg-hand-pointer svg { width: 62px; height: 62px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .lsvg-hand-pointer, .lsvg-start-label { animation: none; }
        }

        .hand-sparkles {
          transform-origin: center;
          animation: sparkleFloat 1.4s ease-in-out infinite;
        }

        @keyframes sparkleFloat {
          0%, 100% { opacity: 0.5; transform: scale(0.85) rotate(0deg); }
          50% { opacity: 1; transform: scale(1.12) rotate(8deg); }
        }
      `}</style>

      <div className={`guided-mode-toggle lsvg-guide-toggle${isGuidedModeEnabled ? " is-on" : ""}`}>
        <span className="lsvg-guide-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l2.2 6.3L20.5 10l-6.3 1.7L12 18l-2.2-6.3L3.5 10l6.3-1.7L12 2z" />
            <path d="M19 14l.9 2.1L22 17l-2.1.9L19 20l-.9-2.1L16 17l2.1-.9L19 14z" />
          </svg>
        </span>
        <span className="lsvg-guide-label">Guided Mode</span>
        <span className="lsvg-guide-status" aria-hidden="true">
          {isGuidedModeEnabled ? "On" : "Off"}
        </span>
        <button
          type="button"
          className={`lsvg-switch${isGuidedModeEnabled ? " is-on" : ""}`}
          onClick={handleGuidedModeToggle}
          aria-pressed={isGuidedModeEnabled}
          aria-label="Guided Mode"
        >
          <span className="lsvg-switch-knob" />
        </button>
      </div>

      {isGuidedModeEnabled && !isLoading && !error && handPosition && (
        <div
          className="guided-helper"
          ref={helperRef}
          style={{
            position: "absolute",
            top: `${handPosition.top}px`,
            left: `${handPosition.left}px`,
            zIndex: 30,
            pointerEvents: "none",
            transition:
              "top 0.7s cubic-bezier(0.22, 1, 0.36, 1), left 0.7s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <div className="guided-hand lsvg-hand-pointer">
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g className="hand-sparkles">
                <path d="M10 18 L11.5 22 L15.5 23.5 L11.5 25 L10 29 L8.5 25 L4.5 23.5 L8.5 22 Z" fill="#FFD84D" />
                <path d="M42 7 L43.5 11 L47.5 12.5 L43.5 14 L42 18 L40.5 14 L36.5 12.5 L40.5 11 Z" fill="#FFD84D" />
                <path d="M56 23 L57.2 26.2 L60.5 27.5 L57.2 28.8 L56 32 L54.8 28.8 L51.5 27.5 L54.8 26.2 Z" fill="#FFD84D" />
                <path d="M19 7 L20 9.5 L22.5 10.5 L20 11.5 L19 14 L18 11.5 L15.5 10.5 L18 9.5 Z" fill="#FFF1A8" />
              </g>
              <path
                d="M27 58c-2 0-3.6-1.1-4.6-2.9L14 40.3c-1.1-2 -0.6-4.5 1.2-5.9 1.7-1.3 4.1-1.1 5.6.5l3.2 3.4V17a4 4 0 0 1 8 0v14.5a3.5 3.5 0 0 1 7 0V33a3.5 3.5 0 0 1 7 0v1.3a3.5 3.5 0 0 1 7 0V44c0 7.7-6.3 14-14 14h-9.9Z"
                fill="#FFC98B"
                stroke="#B5762E"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      )}

      {isGuidedModeEnabled && !isLoading && !error && startPosition && (
        <div
          className="guided-start-label lsvg-start-label"
          aria-hidden="true"
          style={{
            position: "absolute",
            top: `${startPosition.top}px`,
            left: `${startPosition.left}px`,
            zIndex: 31,
            pointerEvents: "none",
          }}
        >
          Start
        </div>
      )}

      <div style={{ height: isLoading ? "600px" : "fit-content", position: "relative" }}>
        {!hasUserInteracted && (
          <div className="start-overlay" style={{ display: "none" }}>
            <button onClick={() => setHasUserInteracted(true)}>Click to Start</button>
          </div>
        )}
        {isLoading ? (
          <BlobLoader />
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <button className="fullscreen-1" id="fullscreenBtn" onClick={handleFullscreen}>
              <i className="fas fa-expand"></i>
            </button>
            <div ref={containerRef} className="svg-container" style={{ width: "100%", height: "auto" }} />
          </>
        )}
      </div>
    </div>
  );
};

export function stopLayeredSVGAudio() {
  stopCurrentAudio();
}

export default LayeredSVG;